import React, { useState, useEffect, useCallback, useRef, useMemo, useImperativeHandle, forwardRef, Fragment } from 'react';
import notify from 'devextreme/ui/notify';
import ReactDOM from 'react-dom';
import AntLocalization from '../../utils/AntLocalization';
import AntAPI from '../../utils/AntAPI';
import AntCondition from '../../utils/AntCondition';
import { isAIWizardAvailable } from '../../utils/AntAIWizard';
import { AntSidebar, AntSession } from '../../utils/AntContext';
import AntAttachmentSection from '../AntAttachmentSection';
import { MOCK_FILTER_GROUPS, MOCK_SAVED_VIEWS, MOCK_STATS, MOCK_REPORT_PARAMS, MOCK_SAVED_REPORTS, MOCK_HISTORY } from './mock-data';

import AntFileViewer from '../AntFileViewer';
import FloatingCommentPanel from '../FloatingCommentPanel';
import AntCommentThread from '../AntCommentThread';
import { sidebarRelativeTime, sidebarDate, sidebarDayMonth } from '../AntRightSidebar/panels/SidebarStandard';
import AIWizardPanel from './AIWizardPanel';
import BrowsePanel from './BrowsePanel';
import './index.scss';
import '../AntRightSidebar/panels/SidebarStandard.scss';
import '../AntRightSidebar/panels/SidebarUnify.scss';

// ─── Browse panel definition (prepended when browseConfig provided) ─
var BROWSE_PANEL = {
    key: 'browse',
    label: { vi: 'Danh sách', en: 'Browse', fr: 'Liste', es: 'Lista', id: 'Daftar', ja: '一覧', zh: '列表', ko: '목록', hi: 'सूची', ar: 'القائمة' },
    iconPath: 'M4 6h16M4 10h16M4 14h16M4 18h16'
};

// ─── Emoji shortcuts ────────────────────────────────────────────
var EMOJI_SHORTCUTS = [
    [':))', '😂'],
    [':-)', '😊'],
    [':)', '🙂'],
    [':(', '😞'],
    [';)', '😉'],
    [':D', '😄'],
    [':-D', '😄'],
    ['XD', '😆'],
    ['xD', '😆'],
    [':P', '😛'],
    [':p', '😛'],
    [':O', '😮'],
    [':o', '😮'],
    ['<3', '❤️'],
    [':*', '😘'],
    ['B)', '😎'],
    [':|', '😐'],
    ['>:(', '😠'],
    [":'(", '😢'],
    ['^_^', '😊'],
    ['-_-', '😑'],
    ['T_T', '😭'],
    [':fire:', '🔥'],
    [':heart:', '❤️'],
    [':thumbsup:', '👍'],
    [':check:', '✅'],
    [':x:', '❌'],
    [':tada:', '🎉']
];
function convertEmojiShortcuts(text) {
    if (!text) return text;
    var result = text;
    for (var i = 0; i < EMOJI_SHORTCUTS.length; i++) {
        while (result.indexOf(EMOJI_SHORTCUTS[i][0]) >= 0) {
            result = result.replace(EMOJI_SHORTCUTS[i][0], EMOJI_SHORTCUTS[i][1]);
        }
    }
    return result;
}

// ─── Config ─────────────────────────────────────────────────────
var MOCK_MODE = false;

var STORAGE_KEY = 'ant_left_sidebar';
var MIN_WIDTH = 220;
var MAX_WIDTH = 400;
var DEFAULT_WIDTH = 250;
var COLLAPSED_WIDTH = 38;

// ─── Panel definitions ──────────────────────────────────────────
var VOUCHER_PANELS = [
    {
        key: 'comment',
        label: { vi: 'Bình luận', en: 'Comments', fr: 'Commentaires', es: 'Comentarios', id: 'Komentar', ja: 'コメント', zh: '评论', ko: '댓글', hi: 'टिप्पणियाँ', ar: 'التعليقات' },
        iconPath: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z',
        visible: {
            whenUserScreen: 'Comments'
        }
    },
    {
        key: 'attachment',
        label: { vi: 'Đính kèm', en: 'Attachments', fr: 'Pièces jointes', es: 'Adjuntos', id: 'Lampiran', ja: '添付', zh: '附件', ko: '첨부', hi: 'संलग्नक', ar: 'المرفقات' },
        iconPath: 'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13',
        visible: {
            when: {
                whenUserScreen: 'Attachments'
            }
        }
    },
    {
        key: 'task',
        label: { vi: 'Công việc', en: 'Tasks', fr: 'Tâches', es: 'Tareas', id: 'Tugas', ja: 'タスク', zh: '任务', ko: '작업', hi: 'कार्य', ar: 'المهام' },
        iconPath:
            'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
        visible: {
            when: {
                whenSys: { use_Module_Task: 1 }
            }
        }
    },
    {
        key: 'aiWizard',
        label: { vi: 'AI Wizard', en: 'AI Wizard', fr: 'Assistant IA', es: 'Asistente IA', id: 'Wizard AI', ja: 'AIウィザード', zh: 'AI 向导', ko: 'AI 마법사', hi: 'AI विज़ार्ड', ar: 'معالج AI' },
        iconPath: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 7h10v10H7z'
        // Visibility xử lý qua isAIWizardAvailable() — gộp 3 điều kiện:
        // aiExtract.enabled + canAnalyze !== false + use_Module_AIWithKey=1.
        // Không khai báo `visible` ở đây để tránh check trùng / nhầm lẫn.
    }
    // { key: 'ai', label: { vi: 'Trợ lý', en: 'Assistant', fr: 'Assistant', es: 'Asistente', id: 'Asisten', ja: 'アシスタント', zh: '助手', ko: '어시스턴트', hi: 'सहायक', ar: 'المساعد' }, iconPath: 'M13 10V3L4 14h7v7l9-11h-7z' },
    // { key: 'history', label: { vi: 'Lịch sử', en: 'History', fr: 'Historique', es: 'Historial', id: 'Riwayat', ja: '履歴', zh: '历史记录', ko: '내역', hi: 'इतिहास', ar: 'السجل' }, iconPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' }
];

var SEARCH_PANELS = [
    {
        key: 'filters',
        label: { vi: 'Bộ lọc', en: 'Filters', fr: 'Filtres', es: 'Filtros', id: 'Filter', ja: 'フィルター', zh: '筛选', ko: '필터', hi: 'फ़िल्टर', ar: 'الفلاتر' },
        iconPath:
            'M3 4a1 1 0 011-1h16a1 1 0 010 2H4a1 1 0 01-1-1zm3 4a1 1 0 011-1h10a1 1 0 010 2H7a1 1 0 01-1-1zm3 4a1 1 0 011-1h4a1 1 0 010 2h-4a1 1 0 01-1-1z'
    },
    { key: 'saved', label: { vi: 'Đã lưu', en: 'Saved', fr: 'Enreg.', es: 'Guardados', id: 'Tersimpan', ja: '保存済み', zh: '已保存', ko: '저장됨', hi: 'सहेजे गए', ar: 'المحفوظات' }, iconPath: 'M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z' },
    {
        key: 'stats',
        label: { vi: 'Phân tích', en: 'Analytics', fr: 'Analyse', es: 'Análisis', id: 'Analisis', ja: '分析', zh: '分析', ko: '분석', hi: 'विश्लेषण', ar: 'تحليل' },
        iconPath:
            'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
    },
    { key: 'ai', label: { vi: 'Trợ lý', en: 'Assistant', fr: 'Assistant', es: 'Asistente', id: 'Asisten', ja: 'アシスタント', zh: '助手', ko: '어시스턴트', hi: 'सहायक', ar: 'المساعد' }, iconPath: 'M13 10V3L4 14h7v7l9-11h-7z' }
];

var REPORT_PANELS = [
    {
        key: 'params',
        label: { vi: 'Tham số', en: 'Parameters', fr: 'Paramètres', es: 'Parámetros', id: 'Parameter', ja: 'パラメータ', zh: '参数', ko: '매개변수', hi: 'पैरामीटर', ar: 'المعاملات' },
        iconPath:
            'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4'
    },
    { key: 'saved', label: { vi: 'Đã lưu', en: 'Saved', fr: 'Enreg.', es: 'Guardados', id: 'Tersimpan', ja: '保存済み', zh: '已保存', ko: '저장됨', hi: 'सहेजे गए', ar: 'المحفوظات' }, iconPath: 'M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z' },
    { key: 'ai', label: { vi: 'Trợ lý', en: 'Assistant', fr: 'Assistant', es: 'Asistente', id: 'Asisten', ja: 'アシスタント', zh: '助手', ko: '어시스턴트', hi: 'सहायक', ar: 'المساعد' }, iconPath: 'M13 10V3L4 14h7v7l9-11h-7z' }
];

// ─── Helpers ────────────────────────────────────────────────────
// Persist sidebar state PER SCREEN (keyed by entityName / voucherType),
// so opening on AROrder doesn't also open on APOrder, GLJournal, etc.
function loadAllStates() {
    try {
        var raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        var parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return {};
        // Backward-compat: legacy flat shape { isOpen, activePanel, width }
        if (parsed.isOpen !== undefined || parsed.activePanel !== undefined || parsed.width !== undefined) {
            return { __default__: parsed };
        }
        return parsed;
    } catch (e) {
        return {};
    }
}

function loadState(screenKey) {
    var all = loadAllStates();
    if (screenKey && all[screenKey]) return all[screenKey];
    return all.__default__ || null;
}

function saveState(screenKey, state) {
    if (!screenKey) return;
    try {
        var all = loadAllStates();
        delete all.__default__;
        all[screenKey] = state;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    } catch (e) {
        /* silent */
    }
}

function SvgIcon(props) {
    return (
        <svg
            width={props.size || 18}
            height={props.size || 18}
            viewBox="0 0 24 24"
            fill="none"
            stroke={props.color || 'currentColor'}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d={props.d} />
        </svg>
    );
}

var chevronRightPath = 'M9 5l7 7-7 7';
var chevronLeftPath = 'M15 19l-7-7 7-7';

// ─── Tooltip button (position: fixed, thoát overflow: hidden) ───
var tooltipStyle = {
    position: 'fixed',
    padding: '4px 10px',
    background: '#1a3561',
    color: '#fff',
    fontSize: '11px',
    whiteSpace: 'nowrap',
    borderRadius: '4px',
    zIndex: 99999,
    pointerEvents: 'none',
    boxShadow: '0 2px 8px rgba(0,0,0,0.18)'
};

function TooltipBtn(props) {
    var [tip, setTip] = useState(null);
    var btnRef = useRef(null);

    var onEnter = useCallback(
        function () {
            if (!props.tooltip || !btnRef.current) return;
            var r = btnRef.current.getBoundingClientRect();
            // side: 'right' = tooltip bên phải, 'left' = bên trái
            var left = props.side === 'left' ? r.left - 6 : r.right + 6;
            var transform = props.side === 'left' ? 'translateX(-100%) translateY(-50%)' : 'translateY(-50%)';
            setTip({ left: left, top: r.top + r.height / 2, transform: transform });
        },
        [props.tooltip, props.side]
    );

    var onLeave = useCallback(function () {
        setTip(null);
    }, []);

    return (
        <button ref={btnRef} className={props.className} onClick={props.onClick} onMouseEnter={onEnter} onMouseLeave={onLeave}>
            {props.children}
            {tip &&
                ReactDOM.createPortal(
                    <div style={Object.assign({}, tooltipStyle, { left: tip.left, top: tip.top, transform: tip.transform })}>{props.tooltip}</div>,
                    document.body
                )}
        </button>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── COMMENT PANEL ───────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

var COMMENT_PAGE_SIZE = 10;

var MOCK_COMMENTS = [
    {
        id: 1,
        user_name: 'Nguyễn Văn An',
        description: 'Đã xác nhận thông tin giao hàng với khách.',
        created_at: Date.now() / 1000 - 3600,
        liked_count: 2,
        liked_user_codes: ['NVA', 'TTB']
    },
    {
        id: 2,
        user_name: 'Trần Thị Bình',
        description: 'Cần kiểm tra lại số lượng sản phẩm A trước khi xuất kho. Tồn kho hiện tại chỉ còn 15.',
        created_at: Date.now() / 1000 - 7200,
        liked_count: 0
    },
    {
        id: 3,
        user_name: 'Lê Quang Dũng',
        description: 'Đã cập nhật giá theo bảng giá mới 2026. 👍',
        created_at: Date.now() / 1000 - 86400,
        liked_count: 1,
        liked_user_codes: ['NVA']
    },
    {
        id: 4,
        user_name: 'Phạm Hoàng Mai',
        description: 'Khách hàng yêu cầu giao trước 15h ngày mai.',
        created_at: Date.now() / 1000 - 172800,
        reply_comment_id: 2,
        reply_user_name: 'Trần Thị Bình',
        reply_description: 'Cần kiểm tra lại số lượng sản phẩm A trước khi xuất kho...',
        liked_count: 0
    },
    {
        id: 5,
        user_name: 'Nguyễn Văn An',
        description: 'OK, sẽ sắp xếp giao đúng hẹn. 🚚',
        created_at: Date.now() / 1000 - 1800,
        meddoc_id: 101,
        meddoc_name: 'Lịch-giao-hàng.pdf',
        liked_count: 0
    },
    {
        id: 6,
        user_name: 'Trần Thị Bình',
        description: 'Đã xác nhận tồn kho đủ.',
        created_at: Date.now() / 1000 - 900,
        reply_comment_id: 5,
        reply_user_name: 'Nguyễn Văn An',
        reply_description: 'OK, sẽ sắp xếp giao đúng hẹn.',
        reply_meddoc_name: 'Lịch-giao-hàng.pdf',
        liked_count: 3,
        liked_user_codes: ['NVA', 'LQD', 'PHM']
    }
];

// ─── Emoji picker data ──────────────────────────────────────────
var EMOJI_CATEGORIES = [
    {
        label: { vi: 'Mặt cười', en: 'Smileys', fr: 'Visages', es: 'Caras', id: 'Wajah', ja: '顔', zh: '表情', ko: '표정', hi: 'चेहरे', ar: 'وجوه' },
        emojis: [
            '😀',
            '😃',
            '😄',
            '😁',
            '😆',
            '😅',
            '🤣',
            '😂',
            '🙂',
            '😊',
            '😇',
            '😍',
            '🥰',
            '😘',
            '😗',
            '😋',
            '😛',
            '😜',
            '🤪',
            '😎',
            '🤩',
            '🥳',
            '😏',
            '😒',
            '😞',
            '😔',
            '😟',
            '😕',
            '🙁',
            '😣',
            '😖',
            '😫',
            '😩',
            '🥺',
            '😤',
            '😠',
            '😡',
            '🤬',
            '😈'
        ]
    },
    {
        label: { vi: 'Cử chỉ', en: 'Gestures', fr: 'Gestes', es: 'Gestos', id: 'Gestur', ja: 'ジェスチャー', zh: '手势', ko: '제스처', hi: 'इशारे', ar: 'إيماءات' },
        emojis: ['👍', '👎', '👏', '🙌', '🤝', '🙏', '💪', '✌️', '🤞', '🤟', '🤘', '👌', '👋', '✋', '🖐️', '👆', '👇', '👈', '👉', '☝️']
    },
    {
        label: { vi: 'Tim & Biểu tượng', en: 'Hearts & Symbols', fr: 'Cœurs et symboles', es: 'Corazones y símbolos', id: 'Hati & simbol', ja: 'ハートと記号', zh: '爱心与符号', ko: '하트와 기호', hi: 'दिल और प्रतीक', ar: 'قلوب ورموز' },
        emojis: [
            '❤️',
            '🧡',
            '💛',
            '💚',
            '💙',
            '💜',
            '🖤',
            '🤍',
            '💯',
            '💢',
            '💥',
            '💫',
            '⭐',
            '🌟',
            '✨',
            '🔥',
            '💡',
            '🎉',
            '🎊',
            '✅',
            '❌',
            '⚠️',
            '💬',
            '📎',
            '📌'
        ]
    }
];

function EmojiPicker(props) {
    var onSelect = props.onSelect;
    var onClose = props.onClose;
    var pickerRef = useRef(null);

    useEffect(
        function () {
            function handleClickOutside(e) {
                if (pickerRef.current && !pickerRef.current.contains(e.target)) onClose();
            }
            document.addEventListener('mousedown', handleClickOutside);
            return function () {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        },
        [onClose]
    );

    return (
        <div className="comment-emoji-picker" ref={pickerRef}>
            {EMOJI_CATEGORIES.map(function (cat, ci) {
                return (
                    <div key={ci} className="emoji-category">
                        <div className="emoji-category-label">{AntLocalization.translateText(cat.label)}</div>
                        <div className="emoji-grid">
                            {cat.emojis.map(function (emoji) {
                                return (
                                    <button
                                        key={emoji}
                                        className="emoji-item"
                                        onClick={function () {
                                            onSelect(emoji);
                                        }}
                                    >
                                        {emoji}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function commentFormatTime(ts) {
    return sidebarRelativeTime(ts);
}

function getInitial(name) {
    if (!name) return '?';
    var parts = name.trim().split(/\s+/);
    return parts[parts.length - 1].charAt(0).toUpperCase();
}

var COMMENT_LOAD_SIZE = 20;

// Comments: one shared thread for every web host (same features as mobile), see AntCommentThread.
function CommentPanel(props) {
    return <AntCommentThread context={props.context} autoFocus={props.autoFocus} />;
}

// ═══════════════════════════════════════════════════════════════════
// ─── ATTACHMENT PANEL ────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

var MOCK_FILES = [
    {
        id: 1,
        name: 'Hợp đồng mua bán - HĐ2024-042.pdf',
        size: 2457600,
        type: 'application/pdf',
        created_at: Date.now() / 1000 - 3600,
        user_name: 'Trần Minh'
    },
    {
        id: 2,
        name: 'Báo giá chi tiết.xlsx',
        size: 184320,
        type: 'application/vnd.ms-excel',
        created_at: Date.now() / 1000 - 7200,
        user_name: 'Nguyễn Hoa'
    },
    { id: 3, name: 'Ảnh sản phẩm mẫu.png', size: 1048576, type: 'image/png', created_at: Date.now() / 1000 - 86400, user_name: 'Lê Quang' },
    { id: 4, name: 'Chứng từ thanh toán.pdf', size: 512000, type: 'application/pdf', created_at: Date.now() / 1000 - 172800, user_name: 'Phạm Dũng' }
];

function formatFileSize(bytes) {
    if (!bytes || bytes === 0) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
}

function getFileExt(name) {
    if (!name) return '';
    var parts = name.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

function getFileTypeLabel(ext) {
    var map = {
        pdf: 'PDF',
        xlsx: 'XLSX',
        xls: 'XLS',
        doc: 'DOC',
        docx: 'DOCX',
        png: 'PNG',
        jpg: 'JPG',
        jpeg: 'JPG',
        gif: 'GIF',
        csv: 'CSV',
        txt: 'TXT',
        zip: 'ZIP'
    };
    return map[ext] || ext.toUpperCase() || 'FILE';
}

function attachmentFormatTime(ts) {
    return sidebarDate(ts, false);
}

function isPreviewable(f) {
    var type = (f.type || '').toLowerCase();
    var sub = (f.sub_type || f.mimetype || '').toLowerCase();
    if (type === 'image' || sub.indexOf('image') >= 0) return 'image';
    if (type === 'video' || sub.indexOf('video') >= 0) return 'video';
    if (type === 'pdf' || sub.indexOf('pdf') >= 0) return 'pdf';
    if (sub.indexOf('audio') >= 0) return 'audio';
    return null;
}

function PreviewOverlay(props) {
    var file = props.file;
    var onClose = props.onClose;
    var previewType = isPreviewable(file);

    useEffect(
        function () {
            function onKey(e) {
                if (e.key === 'Escape') onClose();
            }
            document.addEventListener('keydown', onKey);
            return function () {
                document.removeEventListener('keydown', onKey);
            };
        },
        [onClose]
    );

    return ReactDOM.createPortal(
        <div className="att-preview-overlay" onClick={onClose}>
            <div
                className="att-preview-container"
                onClick={function (e) {
                    e.stopPropagation();
                }}
            >
                <div className="att-preview-header">
                    <span className="att-preview-title">{file.name}</span>
                    <button className="att-preview-close" onClick={onClose}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="att-preview-body">
                    {previewType === 'image' && <img src={file.full_path} alt={file.name} />}
                    {previewType === 'video' && <video src={file.full_path} controls autoPlay />}
                    {previewType === 'audio' && <audio src={file.full_path} controls autoPlay />}
                    {previewType === 'pdf' && <iframe src={file.full_path} title={file.name} />}
                </div>
            </div>
        </div>,
        document.body
    );
}

var MAX_FILE_SIZE_NORMAL = 50 * 1024 * 1024; // 50MB (upload qua server)
var MAX_FILE_SIZE_DIRECT = 2 * 1024 * 1024 * 1024; // 2GB (upload trực tiếp S3)
var DIRECT_UPLOAD_THRESHOLD = 50 * 1024 * 1024; // > 50MB → dùng presigned URL
var BLOCKED_EXTENSIONS = ['exe', 'bat', 'cmd', 'com', 'msi', 'scr', 'pif', 'vbs', 'js', 'wsf', 'sh'];

function validateFiles(fileList) {
    var valid = [];
    var errors = [];
    for (var i = 0; i < fileList.length; i++) {
        var f = fileList[i];
        var ext = (f.name || '').split('.').pop().toLowerCase();
        if (BLOCKED_EXTENSIONS.indexOf(ext) >= 0) {
            errors.push(f.name + ': ' + AntLocalization.translateText({ vi: 'loại file không được phép', en: 'file type not allowed', fr: 'type de fichier non autorisé', es: 'tipo de archivo no permitido', id: 'jenis berkas tidak diizinkan', ja: '許可されていないファイル形式', zh: '不允许的文件类型', ko: '허용되지 않는 파일 형식', hi: 'फ़ाइल प्रकार की अनुमति नहीं', ar: 'نوع الملف غير مسموح' }));
        } else if (f.size > MAX_FILE_SIZE_DIRECT) {
            errors.push(f.name + ': ' + AntLocalization.translateText({ vi: 'vượt quá 2GB', en: 'exceeds 2GB', fr: 'dépasse 2 Go', es: 'supera 2 GB', id: 'melebihi 2GB', ja: '2GBを超えています', zh: '超过 2GB', ko: '2GB 초과', hi: '2GB से अधिक', ar: 'يتجاوز 2 غيغابايت' }));
        } else {
            valid.push(f);
        }
    }
    return { valid: valid, errors: errors };
}

function AttachmentPanel(props) {
    var context = props.context;
    var mockMode = props.mockMode;

    var attachmentAPI = useMemo(function () {
        return new AntAPI('media', 'MEDDocument');
    }, []);

    // Junction service: dùng để LIST các MEDDocument đã attach vào voucher hiện tại
    // (MEDDocument không lưu voucher_id; quan hệ ở MEDDocumentVoucher).
    var attachmentJunctionAPI = useMemo(function () {
        return new AntAPI('media', 'MEDDocumentVoucher');
    }, []);

    var [files, setFiles] = useState([]);
    var [loading, setLoading] = useState(false);
    var [uploading, setUploading] = useState(false);
    var [uploadProgress, setUploadProgress] = useState(0);
    var [previewFile, setPreviewFile] = useState(null);
    var [renamingId, setRenamingId] = useState(null);
    var [renameValue, setRenameValue] = useState('');
    var [dragOver, setDragOver] = useState(false);
    var [uploadError, setUploadError] = useState(null);
    var fileInputRef = useRef(null);
    var panelRef = useRef(null);

    // Current user info
    var sidebarCtx = AntSidebar();
    var currentUserInfo = useMemo(function () {
        try {
            var info = JSON.parse(localStorage.getItem('ANTE-session-info'));
            return info || {};
        } catch (e) {
            return {};
        }
    }, []);
    var currentUserCode = currentUserInfo.user_code || null;
    var isAdmin = currentUserInfo.user_group_code === 'Admin';
    var voucherCreatedBy = context && (context.voucherCreatedUserCode || (context.data && context.data.created_user_code));

    // Voucher status (Cancelled/Deleted/Rejected => block attach actions)
    var voucherStatus = context && context.data && (context.data.status || context.data.state) || null;
    var BLOCKED_ATTACH_STATUSES = ['Cancelled', 'Deleted', 'Rejected'];
    var isAttachBlocked = voucherStatus && BLOCKED_ATTACH_STATUSES.indexOf(voucherStatus) >= 0;

    // ─── Load ───────────────────────────────────────────────────
    // Dùng MEDDocumentVoucher.list (junction) thay vì MEDDocument.list?voucher_type
    // (endpoint legacy không lọc theo voucher_id chính xác). Mỗi row trả document_*
    // được map sang MEDDocument shape để UI render giữ nguyên.
    var loadFiles = useCallback(
        function () {
            if (mockMode || !context || !context.voucherType) return;
            setLoading(true);
            var listBody = {
                voucher_type: context.voucherType,
                pageSize: 100,
                pageIndex: 0
            };
            if (context.voucherId) listBody.voucher_id = context.voucherId;
            else if (context.voucherCode) listBody.voucher_code = context.voucherCode;
            attachmentJunctionAPI.post(
                '/' + attachmentJunctionAPI.routeURL + '/list',
                listBody,
                function (result) {
                    var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
                    var mapped = (rows instanceof Array ? rows : []).map(function (r) {
                        return {
                            id: r.document_id,
                            code: r.document_code,
                            name: r.document_name,
                            type: r.document_type,
                            attached_at: r.attached_at,
                            voucher_attachment_id: r.id,
                            voucher_type: r.voucher_type,
                            voucher_id: r.voucher_id,
                            voucher_code: r.voucher_code,
                            source: r.source
                        };
                    });
                    setFiles(mapped);
                    setLoading(false);
                }
            );
        },
        [context, mockMode, attachmentJunctionAPI]
    );

    useEffect(
        function () {
            if (!mockMode) loadFiles();
        },
        [context ? context.voucherId : null]
    );

    // Socket sync — auto reload khi attach/detach xảy ra ở AntAttachmentList
    // (inline trên form). Dùng chung event key với AttachmentList.
    var attachSocketSession = AntSession ? AntSession() : null;
    var loadFilesRef = useRef(loadFiles);
    loadFilesRef.current = loadFiles;
    useEffect(function () {
        if (!attachSocketSession || !attachSocketSession.registerSocketListener || !context || !context.voucherType) return;
        var key = 'AttachmentPanel-' + context.voucherType + '-' + (context.voucherCode || context.voucherId || '');
        var handler = function (data) {
            if (!data) return false;
            if (data.event !== 'MEDDocumentVoucher-attached' && data.event !== 'MEDDocumentVoucher-detached') return false;
            if (data.voucher_type !== context.voucherType) return true;
            if (context.voucherCode && data.voucher_code && data.voucher_code !== context.voucherCode) return true;
            if (context.voucherId && data.voucher_id && String(data.voucher_id) !== String(context.voucherId)) return true;
            loadFilesRef.current();
            return true;
        };
        attachSocketSession.registerSocketListener(handler, key);
        return function () { attachSocketSession.clearSocketListener(key); };
    }, [attachSocketSession, context ? context.voucherType : null, context ? context.voucherCode : null, context ? context.voucherId : null]);

    // ─── Upload core (dùng chung cho click, drag-drop, paste) ───
    // Upload nhỏ qua server (multer → S3)
    var voucherAPI = useMemo(
        function () {
            if (!context || !context.voucherType) return null;
            return new AntAPI(context.voucherModule || 'ar', context.voucherType);
        },
        [context ? context.voucherType : null, context ? context.voucherModule : null]
    );

    var doUploadViaServer = useCallback(
        function (files) {
            if (!context || !context.voucherType) return;
            setUploading(true);
            setUploadProgress(0);
            var formData = new FormData();
            for (var j = 0; j < files.length; j++) {
                formData.append('files', files[j]);
            }
            // NB3: Upload route qua media/MEDDocument/upload + truyền voucher context trong FormData.
            // BE actionUploadDocuments đã handle voucher_type/voucher_id/voucher_code → tạo junction MEDDocumentVoucher.
            formData.append('voucher_type', context.voucherType);
            if (context.voucherCode) formData.append('voucher_code', context.voucherCode);
            if (context.voucherId) formData.append('voucher_id', context.voucherId);
            attachmentAPI.upload('/' + attachmentAPI.routeURL + '/upload', formData, function (result) {
                setUploading(false);
                setUploadProgress(0);
                if (result && result.error) {
                    setUploadError(AntLocalization.translateText({ vi: 'Lỗi upload', en: 'Upload failed', fr: 'Échec de l\'envoi', es: 'La carga falló', id: 'Unggahan gagal', ja: 'アップロード失敗', zh: '上传失败', ko: '업로드 실패', hi: 'अपलोड विफल', ar: 'فشل الرفع' }));
                    setTimeout(function () {
                        setUploadError(null);
                    }, 3000);
                } else {
                    loadFiles();
                }
            });
        },
        [context, attachmentAPI, loadFiles]
    );

    // Upload lớn trực tiếp S3 (presigned URL, từng file)
    var doUploadDirect = useCallback(
        function (files) {
            var voucherRoute = (context.voucherModule || 'ar') + '/' + context.voucherType;
            var voucherAPI = new AntAPI(context.voucherModule || 'ar', context.voucherType);
            var totalFiles = files.length;
            var doneCount = 0;

            setUploading(true);
            setUploadProgress(0);

            for (var i = 0; i < totalFiles; i++) {
                (function (file) {
                    voucherAPI.uploadDirect(
                        voucherAPI.routeURL,
                        file,
                        context.voucherCode,
                        function (pct) {
                            // Progress cho file hiện tại + files đã xong
                            setUploadProgress(Math.round(((doneCount + pct / 100) / totalFiles) * 100));
                        },
                        function (result) {
                            doneCount++;
                            if (result && result.error) {
                                setUploadError(file.name + ': ' + (result.message || 'Upload failed'));
                                setTimeout(function () {
                                    setUploadError(null);
                                }, 5000);
                            }
                            if (doneCount >= totalFiles) {
                                setUploading(false);
                                setUploadProgress(0);
                                loadFiles();
                            }
                        }
                    );
                })(files[i]);
            }
        },
        [context]
    );

    var doUpload = useCallback(
        function (fileList) {
            if (!context || !context.voucherType || !fileList || fileList.length === 0) return;
            var result = validateFiles(fileList);
            if (result.errors.length > 0) {
                setUploadError(result.errors.join('\n'));
                setTimeout(function () {
                    setUploadError(null);
                }, 5000);
            }
            if (result.valid.length === 0) return;

            // Phân loại: file nhỏ qua server, file lớn qua presigned URL
            var smallFiles = [];
            var largeFiles = [];
            for (var i = 0; i < result.valid.length; i++) {
                if (result.valid[i].size > DIRECT_UPLOAD_THRESHOLD) largeFiles.push(result.valid[i]);
                else smallFiles.push(result.valid[i]);
            }

            if (smallFiles.length > 0 && largeFiles.length === 0) {
                doUploadViaServer(smallFiles);
            } else if (largeFiles.length > 0 && smallFiles.length === 0) {
                doUploadDirect(largeFiles);
            } else {
                // Có cả 2 loại: upload nhỏ trước, xong rồi upload lớn
                doUploadViaServer(smallFiles);
                // Large files upload song song (presigned URL không qua server)
                doUploadDirect(largeFiles);
            }
        },
        [context, doUploadViaServer, doUploadDirect]
    );

    // ─── Click upload ───────────────────────────────────────────
    // NB6: Guard isAttachBlocked ở mọi path (click button, file input, drag-drop)
    var handleUploadClick = useCallback(function () {
        if (isAttachBlocked) {
            setUploadError(
                AntLocalization.translateText({
                    vi: 'Phiếu đã ' + (voucherStatus === 'Cancelled' ? 'huỷ' : voucherStatus === 'Deleted' ? 'xoá' : 'từ chối') + ', không thể đính kèm tài liệu',
                    en: 'Voucher is ' + voucherStatus + ', cannot attach documents'
                })
            );
            setTimeout(function () { setUploadError(null); }, 4000);
            return;
        }
        if (fileInputRef.current) fileInputRef.current.click();
    }, [isAttachBlocked, voucherStatus]);

    var handleFileChange = useCallback(
        function (e) {
            if (isAttachBlocked) {
                setUploadError(
                    AntLocalization.translateText({
                        vi: 'Phiếu đã ' + (voucherStatus === 'Cancelled' ? 'huỷ' : voucherStatus === 'Deleted' ? 'xoá' : 'từ chối') + ', không thể đính kèm tài liệu',
                        en: 'Voucher is ' + voucherStatus + ', cannot attach documents'
                    })
                );
                setTimeout(function () { setUploadError(null); }, 4000);
                e.target.value = '';
                return;
            }
            if (e.target.files && e.target.files.length > 0) doUpload(e.target.files);
            e.target.value = '';
        },
        [doUpload, isAttachBlocked, voucherStatus]
    );

    // ─── Drag & Drop ────────────────────────────────────────────
    var handleDragOver = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
    }, []);
    var handleDragLeave = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    }, []);
    var handleDrop = useCallback(
        function (e) {
            e.preventDefault();
            e.stopPropagation();
            setDragOver(false);
            // ─── Block attach khi voucher đã Cancelled/Deleted/Rejected ─────
            if (isAttachBlocked) {
                console.warn('[AttachmentPanel] Voucher status ' + voucherStatus + ' không cho phép attach');
                setUploadError(
                    AntLocalization.translateText({
                        vi: 'Phiếu đã ' + (voucherStatus === 'Cancelled' ? 'huỷ' : voucherStatus === 'Deleted' ? 'xoá' : 'từ chối') + ', không thể đính kèm tài liệu',
                        en: 'Voucher is ' + voucherStatus + ', cannot attach documents'
                    })
                );
                setTimeout(function () { setUploadError(null); }, 4000);
                return;
            }
            // ─── application/x-meddoc: existing MED document từ rightSidebar panel ──
            try {
                if (
                    e.dataTransfer &&
                    e.dataTransfer.types &&
                    Array.prototype.indexOf.call(e.dataTransfer.types, 'application/x-meddoc') >= 0
                ) {
                    var payload = JSON.parse(e.dataTransfer.getData('application/x-meddoc'));
                    if (context && context.voucherType && context.voucherId) {
                        attachmentAPI.post(
                            '/' + attachmentAPI.routeURL + '/actionAttachToVoucher',
                            {
                                document_id: payload.document_id,
                                document_code: payload.document_code,
                                voucher_type: context.voucherType,
                                voucher_id: context.voucherId,
                                voucher_code: context.voucherCode,
                                source: 'DragDrop'
                            },
                            function (r) {
                                if (r && (r.ok || !r.error)) {
                                    loadFiles();
                                }
                            }
                        );
                    }
                    return;
                }
            } catch (err) {
                console.error('[AntLeftSidebar attachment drop x-meddoc] error:', err && err.message);
            }
            if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                doUpload(e.dataTransfer.files);
            }
        },
        [doUpload, context, attachmentAPI, loadFiles, isAttachBlocked, voucherStatus]
    );

    // ─── Clipboard paste ────────────────────────────────────────
    useEffect(
        function () {
            function onPaste(e) {
                if (!panelRef.current || !panelRef.current.contains(document.activeElement)) return;
                var items = e.clipboardData && e.clipboardData.items;
                if (!items) return;
                var pasteFiles = [];
                for (var i = 0; i < items.length; i++) {
                    if (items[i].kind === 'file') {
                        var f = items[i].getAsFile();
                        if (f) pasteFiles.push(f);
                    }
                }
                if (pasteFiles.length > 0) {
                    e.preventDefault();
                    doUpload(pasteFiles);
                }
            }
            document.addEventListener('paste', onPaste);
            return function () {
                document.removeEventListener('paste', onPaste);
            };
        },
        [doUpload]
    );

    // ─── Detach (gỡ junction MEDDocumentVoucher) ─────────────────
    // NB5: handleDelete intent = gỡ đính kèm khỏi voucher, KHÔNG xoá file gốc.
    // File vẫn ở MEDDocument; chỉ junction row bị xoá.
    var handleDelete = useCallback(function (f) {
        if (!context || !context.voucherId) return;
        attachmentAPI.post(
            '/' + attachmentAPI.routeURL + '/actionDetachFromVoucher',
            { document_id: f.id, voucher_id: context.voucherId },
            function (r) {
                if (r && r.error) {
                    setUploadError(r.message || AntLocalization.translateText({ vi: 'Lỗi gỡ đính kèm', en: 'Detach failed', fr: 'Échec du retrait', es: 'Error al quitar', id: 'Gagal melepas', ja: '添付解除に失敗', zh: '移除附件失败', ko: '첨부 해제 실패', hi: 'संलग्नक हटाना विफल', ar: 'فشل إزالة المرفق' }));
                    setTimeout(function () { setUploadError(null); }, 4000);
                } else {
                    loadFiles();
                }
            }
        );
    }, [attachmentAPI, context, loadFiles]);

    // ─── Rename (chỉ sửa tên, giữ nguyên extension) ──────────
    var [renameExt, setRenameExt] = useState('');
    var startRename = useCallback(function (f) {
        var name = f.name || '';
        var dotIdx = name.lastIndexOf('.');
        if (dotIdx > 0) {
            setRenameValue(name.substring(0, dotIdx));
            setRenameExt(name.substring(dotIdx));
        } else {
            setRenameValue(name);
            setRenameExt('');
        }
        setRenamingId(f.id);
    }, []);

    var submitRename = useCallback(
        function (f) {
            var baseName = renameValue.trim();
            if (!baseName) {
                setRenamingId(null);
                return;
            }
            var fullName = baseName + renameExt;
            if (fullName === f.name) {
                setRenamingId(null);
                return;
            }
            attachmentAPI.post('/' + attachmentAPI.routeURL + '/update', { where: { id: f.id }, update: { name: fullName } }, function () {
                setRenamingId(null);
                loadFiles();
            });
        },
        [renameValue, renameExt]
    );

    var handleRenameKeyDown = useCallback(
        function (e, f) {
            if (e.key === 'Enter') {
                e.preventDefault();
                submitRename(f);
            }
            if (e.key === 'Escape') {
                setRenamingId(null);
            }
        },
        [submitRename]
    );

    // ─── Preview (dùng AntFileViewer) ─────────────────────────
    var handlePreview = useCallback(
        function (f) {
            if (!f.full_path) return;
            var viewerFiles = files
                .filter(function (x) {
                    return x.full_path;
                })
                .map(function (x) {
                    return { name: x.name, url: x.full_path, type: x.sub_type || x.type || '' };
                });
            var targetIdx = 0;
            for (var i = 0; i < viewerFiles.length; i++) {
                if (viewerFiles[i].url === f.full_path) {
                    targetIdx = i;
                    break;
                }
            }
            setPreviewFile({ files: viewerFiles, index: targetIdx });
        },
        [files]
    );

    // ─── Empty ──────────────────────────────────────────────────
    if (!mockMode && (!context || !context.voucherType)) {
        return (
            <div className="sidebar-panel-empty">
                <div className="empty-text">
                    {AntLocalization.translateText({ vi: 'Mở chứng từ để xem tài liệu đính kèm', en: 'Open a document to see attachments', fr: 'Ouvrez un document pour voir les pièces jointes', es: 'Abra un documento para ver los adjuntos', id: 'Buka dokumen untuk melihat lampiran', ja: '伝票を開くと添付ファイルが表示されます', zh: '打开单据以查看附件', ko: '문서를 열면 첨부 파일을 볼 수 있습니다', hi: 'संलग्नक देखने के लिए दस्तावेज़ खोलें', ar: 'افتح مستندًا لعرض المرفقات' })}
                </div>
            </div>
        );
    }

    return (
        <div className="attachment-panel" ref={panelRef} tabIndex={-1} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
            {/* Upload area — NB6: disabled khi voucher đã Cancelled/Deleted/Rejected */}
            <div
                className={'attachment-upload-area' + (dragOver ? ' drag-over' : '') + (isAttachBlocked ? ' disabled' : '')}
                onClick={handleUploadClick}
                title={isAttachBlocked ? AntLocalization.translateText({ vi: 'Phiếu đã ' + voucherStatus + ' — không thể đính kèm', en: 'Voucher is ' + voucherStatus + ' — cannot attach' }) : undefined}
            >
                <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFileChange} disabled={isAttachBlocked} />
                {uploading ? (
                    <div className="att-upload-progress">
                        <div className="att-progress-bar">
                            <div className="att-progress-fill" style={{ width: uploadProgress + '%' }} />
                        </div>
                        <span className="att-progress-text">{uploadProgress}%</span>
                    </div>
                ) : dragOver ? (
                    <span>{AntLocalization.translateText({ vi: 'Thả file vào đây', en: 'Drop files here', fr: 'Déposez les fichiers', es: 'Suelte archivos aquí', id: 'Letakkan berkas di sini', ja: 'ここにドロップ', zh: '将文件拖放到此处', ko: '여기에 파일을 놓으세요', hi: 'फ़ाइलें यहाँ छोड़ें', ar: 'أفلت الملفات هنا' })}</span>
                ) : (
                    <>
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M12 5v14M5 12h14" />
                        </svg>
                        <span>{AntLocalization.translateText({ vi: 'Thêm tài liệu (hoặc kéo thả)', en: 'Add file (or drag & drop)', fr: 'Ajouter un fichier (ou glisser-déposer)', es: 'Añadir archivo (o arrastrar y soltar)', id: 'Tambah berkas (atau seret & lepas)', ja: 'ファイルを追加（ドラッグ＆ドロップ可）', zh: '添加文件（或拖放）', ko: '파일 추가 (또는 끌어서 놓기)', hi: 'फ़ाइल जोड़ें (या खींचकर छोड़ें)', ar: 'إضافة ملف (أو السحب والإفلات)' })}</span>
                    </>
                )}
            </div>

            {/* Error message */}
            {uploadError && <div className="att-error">{uploadError}</div>}

            {/* File list */}
            <div className="attachment-list">
                {loading && <div className="attachment-loading">{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '불러오는 중...', hi: 'लोड हो रहा है...', ar: 'جارٍ التحميل...' })}</div>}
                {!loading && files.length === 0 && (
                    <div className="attachment-empty">{AntLocalization.translateText({ vi: 'Chưa có tài liệu', en: 'No attachments', fr: 'Aucune pièce jointe', es: 'Sin adjuntos', id: 'Belum ada lampiran', ja: 'ファイルなし', zh: '暂无附件', ko: '첨부 파일이 없습니다', hi: 'कोई संलग्नक नहीं', ar: 'لا توجد مرفقات' })}</div>
                )}
                {files.map(function (f) {
                    var ext = f.extension || getFileExt(f.name);
                    var typeLabel = f.type || getFileTypeLabel(ext);
                    var hasUrl = !!f.full_path;
                    var imgPreview = (f.sub_type || f.type || '').indexOf('image') >= 0 || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(f.name);
                    var isRenaming = renamingId === f.id;

                    return (
                        <div key={f.id} className="attachment-item">
                            <div
                                className={'attachment-icon' + (hasUrl ? ' previewable' : '')}
                                onClick={function () {
                                    handlePreview(f);
                                }}
                            >
                                {imgPreview && f.full_path ? (
                                    <img className="attachment-thumb" src={f.full_path} alt="" />
                                ) : (
                                    <span className="attachment-ext">{typeLabel}</span>
                                )}
                            </div>
                            <div className="attachment-info">
                                {isRenaming ? (
                                    <div className="attachment-rename-row">
                                        <input
                                            className="attachment-rename-input"
                                            value={renameValue}
                                            onChange={function (e) {
                                                setRenameValue(e.target.value);
                                            }}
                                            onKeyDown={function (e) {
                                                handleRenameKeyDown(e, f);
                                            }}
                                            onBlur={function () {
                                                submitRename(f);
                                            }}
                                            autoFocus
                                        />
                                        {renameExt && <span className="attachment-rename-ext">{renameExt}</span>}
                                    </div>
                                ) : (
                                    <div
                                        className="attachment-name"
                                        title={f.name}
                                        onClick={function () {
                                            handlePreview(f);
                                        }}
                                    >
                                        {f.name}
                                    </div>
                                )}
                                <div className="attachment-meta">
                                    {f.file_size && <span>{formatFileSize(f.file_size)}</span>}
                                    {(f.created_user_name || f.user_name) && <span>{f.created_user_name || f.user_name}</span>}
                                    <span>{attachmentFormatTime(f.created_at)}</span>
                                </div>
                            </div>
                            <div className="attachment-actions">
                                {f.full_path && (
                                    <a
                                        className="att-action-btn"
                                        href={f.full_path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={AntLocalization.translateText({ vi: 'Tải xuống', en: 'Download', fr: 'Télécharger', es: 'Descargar', id: 'Unduh', ja: 'ダウンロード', zh: '下载', ko: '다운로드', hi: 'डाउनलोड करें', ar: 'تنزيل' })}
                                    >
                                        <svg
                                            width="12"
                                            height="12"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                                        </svg>
                                    </a>
                                )}
                                {!isRenaming && (
                                    <button
                                        className="att-action-btn"
                                        onClick={function () {
                                            startRename(f);
                                        }}
                                        title={AntLocalization.translateText({ vi: 'Đổi tên', en: 'Rename', fr: 'Renommer', es: 'Renombrar', id: 'Ganti nama', ja: '名前を変更', zh: '重命名', ko: '이름 바꾸기', hi: 'नाम बदलें', ar: 'إعادة تسمية' })}
                                    >
                                        <svg
                                            width="12"
                                            height="12"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                    </button>
                                )}
                                <button
                                    className="att-action-btn att-delete-btn"
                                    onClick={function () {
                                        handleDelete(f);
                                    }}
                                    title={AntLocalization.translateText({ vi: 'Xóa', en: 'Delete', fr: 'Supprimer', es: 'Eliminar', id: 'Hapus', ja: '削除', zh: '删除', ko: '삭제', hi: 'हटाएँ', ar: 'حذف' })}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {previewFile && (
                <AntFileViewer
                    visible
                    files={previewFile.files}
                    initialIndex={previewFile.index}
                    onClose={function () {
                        setPreviewFile(null);
                    }}
                />
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── TASK PANEL ──────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

var MOCK_TASKS = [
    {
        id: 1,
        code: 'TSK-0051',
        title: 'Xác nhận thông tin giao hàng',
        task_status: 'WIP',
        user_name: 'Trần Minh',
        due_date: Date.now() / 1000 + 86400
    },
    {
        id: 2,
        code: 'TSK-0052',
        title: 'Kiểm tra tồn kho sản phẩm A',
        task_status: 'Done',
        user_name: 'Nguyễn Hoa',
        due_date: Date.now() / 1000 - 3600
    },
    {
        id: 3,
        code: 'TSK-0053',
        title: 'Liên hệ khách hàng xác nhận đơn',
        task_status: 'ToDo',
        user_name: 'Lê Quang',
        due_date: Date.now() / 1000 + 172800
    }
];

var TASK_STATUS_MAP = {
    New: { label: { vi: 'Mới', en: 'New', fr: 'Nouveau', es: 'Nuevo', id: 'Baru', ja: '新規', zh: '新建', ko: '신규', hi: 'नया', ar: 'جديد' }, color: '#94a3b8' },
    ToDo: { label: { vi: 'Cần làm', en: 'To Do', fr: 'À faire', es: 'Por hacer', id: 'Tertunda', ja: '未着手', zh: '待办', ko: '할 일', hi: 'करना है', ar: 'للتنفيذ' }, color: '#5b8fd9' },
    WIP: { label: { vi: 'Đang làm', en: 'In Progress', fr: 'En cours', es: 'En curso', id: 'Dikerjakan', ja: '進行中', zh: '进行中', ko: '진행 중', hi: 'प्रगति में', ar: 'قيد التنفيذ' }, color: '#c88a2e' },
    Done: { label: { vi: 'Hoàn thành', en: 'Done', fr: 'Terminé', es: 'Hecho', id: 'Selesai', ja: '完了', zh: '已完成', ko: '완료', hi: 'पूर्ण', ar: 'مكتمل' }, color: '#3da06a' },
    Failed: { label: { vi: 'Thất bại', en: 'Failed', fr: 'Échoué', es: 'Fallido', id: 'Gagal', ja: '失敗', zh: '失败', ko: '실패', hi: 'विफल', ar: 'فشل' }, color: '#c75050' },
    Close: { label: { vi: 'Đóng', en: 'Closed', fr: 'Clôturé', es: 'Cerrado', id: 'Ditutup', ja: '完了済み', zh: '已关闭', ko: '종료됨', hi: 'बंद', ar: 'مغلق' }, color: '#64748b' },
    Cancel: { label: { vi: 'Hủy', en: 'Cancelled', fr: 'Annulé', es: 'Cancelado', id: 'Dibatalkan', ja: '取消済', zh: '已取消', ko: '취소됨', hi: 'रद्द', ar: 'ملغى' }, color: '#94a3b8' }
};

function taskFormatDate(ts) {
    return sidebarDayMonth(ts);
}

var TASK_TYPE_OPTIONS = [
    { code: 'Task', label: { vi: 'Thực hiện', en: 'Execute', fr: 'Exécuter', es: 'Ejecutar', id: 'Kerjakan', ja: '実行', zh: '执行', ko: '수행', hi: 'निष्पादित करें', ar: 'تنفيذ' } },
    { code: 'Feature', label: { vi: 'Phát triển', en: 'Develop', fr: 'Développer', es: 'Desarrollar', id: 'Kembangkan', ja: '開発', zh: '开发', ko: '개발', hi: 'विकास', ar: 'تطوير' } },
    { code: 'Bug', label: { vi: 'Xử lý lỗi', en: 'Fix Bug', fr: 'Corriger bug', es: 'Corregir error', id: 'Perbaiki bug', ja: 'バグ修正', zh: '修复缺陷', ko: '버그 수정', hi: 'बग ठीक करें', ar: 'إصلاح خطأ' } },
    { code: 'Improvement', label: { vi: 'Cải tiến', en: 'Improve', fr: 'Améliorer', es: 'Mejorar', id: 'Tingkatkan', ja: '改善', zh: '改进', ko: '개선', hi: 'सुधार', ar: 'تحسين' } },
    { code: 'Issue', label: { vi: 'Giải quyết sự cố', en: 'Resolve Issue', fr: 'Résoudre un incident', es: 'Resolver incidencia', id: 'Selesaikan masalah', ja: '問題解決', zh: '解决问题', ko: '이슈 해결', hi: 'समस्या हल करें', ar: 'حل مشكلة' } },
    { code: 'Inspection', label: { vi: 'Kiểm tra/QC', en: 'Inspect/QC', fr: 'Inspection/QC', es: 'Inspección/QC', id: 'Inspeksi/QC', ja: '検査/QC', zh: '检验/QC', ko: '검사/QC', hi: 'निरीक्षण/QC', ar: 'فحص/QC' } },
    { code: 'Milestone', label: { vi: 'Cột mốc', en: 'Milestone', fr: 'Jalon', es: 'Hito', id: 'Tonggak', ja: 'マイルストーン', zh: '里程碑', ko: '마일스톤', hi: 'माइलस्टोन', ar: 'مرحلة رئيسية' } },
    { code: 'ChangeRequest', label: { vi: 'Yêu cầu thay đổi', en: 'Change Request', fr: 'Changement', es: 'Solicitud de cambio', id: 'Permintaan perubahan', ja: '変更要求', zh: '变更请求', ko: '변경 요청', hi: 'परिवर्तन अनुरोध', ar: 'طلب تغيير' } }
];
var PRIORITY_OPTIONS = [
    { code: 'Low', label: { vi: 'Thấp', en: 'Low', fr: 'Basse', es: 'Baja', id: 'Rendah', ja: '低', zh: '低', ko: '낮음', hi: 'कम', ar: 'منخفضة' } },
    { code: 'Normal', label: { vi: 'Bình thường', en: 'Normal', fr: 'Normale', es: 'Normal', id: 'Normal', ja: '通常', zh: '普通', ko: '보통', hi: 'सामान्य', ar: 'عادية' } },
    { code: 'High', label: { vi: 'Cao', en: 'High', fr: 'Haute', es: 'Alta', id: 'Tinggi', ja: '高', zh: '高', ko: '높음', hi: 'उच्च', ar: 'عالية' } },
    { code: 'Urgent', label: { vi: 'Khẩn cấp', en: 'Urgent', fr: 'Urgente', es: 'Urgente', id: 'Mendesak', ja: '緊急', zh: '紧急', ko: '긴급', hi: 'अत्यावश्यक', ar: 'عاجلة' } }
];
var SEVERITY_OPTIONS = [
    { code: 'Trivial', label: { vi: 'Không đáng kể', en: 'Trivial', fr: 'Négligeable', es: 'Trivial', id: 'Sepele', ja: '軽微', zh: '轻微', ko: '사소함', hi: 'नगण्य', ar: 'طفيفة' } },
    { code: 'Minor', label: { vi: 'Nhỏ', en: 'Minor', fr: 'Mineure', es: 'Menor', id: 'Minor', ja: '小', zh: '次要', ko: '경미', hi: 'मामूली', ar: 'ثانوية' } },
    { code: 'Normal', label: { vi: 'Bình thường', en: 'Normal', fr: 'Normale', es: 'Normal', id: 'Normal', ja: '通常', zh: '普通', ko: '보통', hi: 'सामान्य', ar: 'عادية' } },
    { code: 'Major', label: { vi: 'Quan trọng', en: 'Major', fr: 'Majeure', es: 'Mayor', id: 'Mayor', ja: '重要', zh: '重要', ko: '중요', hi: 'प्रमुख', ar: 'رئيسية' } },
    { code: 'Critical', label: { vi: 'Nghiêm trọng', en: 'Critical', fr: 'Critique', es: 'Crítica', id: 'Kritis', ja: '重大', zh: '严重', ko: '심각', hi: 'गंभीर', ar: 'حرجة' } },
    { code: 'Blocker', label: { vi: 'Chặn', en: 'Blocker', fr: 'Bloquante', es: 'Bloqueante', id: 'Pemblokir', ja: 'ブロッカー', zh: '阻断', ko: '차단', hi: 'अवरोधक', ar: 'معيقة' } }
];
var DELIVERY_OPTIONS = [
    { code: 'None', label: { vi: 'Không yêu cầu', en: 'None', fr: 'Aucun', es: 'Ninguno', id: 'Tidak ada', ja: 'なし', zh: '无', ko: '없음', hi: 'कोई नहीं', ar: 'لا شيء' } },
    { code: 'Report', label: { vi: 'Báo cáo', en: 'Report', fr: 'Rapport', es: 'Informe', id: 'Laporan', ja: 'レポート', zh: '报告', ko: '보고서', hi: 'रिपोर्ट', ar: 'تقرير' } },
    { code: 'Attachment', label: { vi: 'Đính kèm file', en: 'Attachment', fr: 'Pièce jointe', es: 'Adjunto', id: 'Lampiran', ja: '添付ファイル', zh: '附件', ko: '첨부 파일', hi: 'संलग्नक', ar: 'مرفق' } },
    { code: 'Approval', label: { vi: 'Cần duyệt', en: 'Approval', fr: 'Approbation', es: 'Aprobación', id: 'Persetujuan', ja: '承認', zh: '审批', ko: '승인', hi: 'अनुमोदन', ar: 'الموافقة' } }
];

function getStatusColor(status) {
    return TASK_STATUS_MAP[status] ? TASK_STATUS_MAP[status].color : '#94a3b8';
}
function getStatusLabel(status) {
    return TASK_STATUS_MAP[status] ? AntLocalization.translateText(TASK_STATUS_MAP[status].label) : status;
}
function getTaskTypeLabel(taskType) {
    var opt = TASK_TYPE_OPTIONS.find(function (o) {
        return o.code === taskType;
    });
    return opt ? AntLocalization.translateText(opt.label) : taskType;
}

function TaskDropdown(props) {
    var options = props.options;
    var value = props.value;
    var onChange = props.onChange;
    var className = props.className || '';
    var [open, setOpen] = useState(false);
    var ref = useRef(null);

    var selected = options.find(function (o) {
        return o.code === value;
    });
    var label = selected ? AntLocalization.translateText(selected.label) : value || '';

    useEffect(
        function () {
            if (!open) return;
            var handleClickOutside = function (e) {
                if (ref.current && !ref.current.contains(e.target)) setOpen(false);
            };
            document.addEventListener('mousedown', handleClickOutside);
            return function () {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        },
        [open]
    );

    return (
        <div className={'task-dropdown' + (className ? ' ' + className : '') + (open ? ' open' : '')} ref={ref}>
            <button
                className="task-dropdown-trigger"
                onClick={function () {
                    setOpen(!open);
                }}
                type="button"
            >
                <span className="task-dropdown-label">{label}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>
            {open && (
                <div className="task-dropdown-menu">
                    {options.map(function (o) {
                        var isActive = o.code === value;
                        return (
                            <div
                                key={o.code}
                                className={'task-dropdown-item' + (isActive ? ' active' : '')}
                                onClick={function () {
                                    onChange(o.code);
                                    setOpen(false);
                                }}
                            >
                                <span>{AntLocalization.translateText(o.label)}</span>
                                {isActive && (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function renderSelect(options, value, onChange, classOrStyle) {
    var props = typeof classOrStyle === 'string' ? { className: classOrStyle } : { style: classOrStyle };
    return (
        <select {...props} value={value || ''} onChange={onChange}>
            {options.map(function (o) {
                return (
                    <option key={o.code} value={o.code}>
                        {AntLocalization.translateText(o.label)}
                    </option>
                );
            })}
        </select>
    );
}

// ─── Reusable user picker field ─────────────────────────────────
function TaskUserField(props) {
    var userCode = props.userCode;
    var userName = props.userName;
    var onSelect = props.onSelect;
    var onClear = props.onClear;

    var [localSearch, setLocalSearch] = useState('');
    var [localResults, setLocalResults] = useState([]);
    var [localLoading, setLocalLoading] = useState(false);
    var [showList, setShowList] = useState(false);
    var localTimer = useRef(null);
    var wrapRef = useRef(null);
    var userAPI = useMemo(function () {
        return new AntAPI('user', 'ACUserAccount');
    }, []);

    var doSearch = useCallback(
        function (text) {
            setLocalLoading(true);
            var params = { pageSize: 30, pageIndex: 0 };
            if (text && text.length > 0) params.keyword = text;
            userAPI.post('/' + userAPI.routeURL + '/list', params, function (result) {
                var rows =
                    (result && result.data && result.data.rows) ||
                    (result && result.rows) ||
                    (result && result.data instanceof Array ? result.data : []);
                setLocalResults(rows instanceof Array ? rows : []);
                setLocalLoading(false);
            });
        },
        [userAPI]
    );

    var handleSearchChange = useCallback(
        function (e) {
            var val = e.target.value;
            setLocalSearch(val);
            if (localTimer.current) clearTimeout(localTimer.current);
            localTimer.current = setTimeout(function () {
                doSearch(val.trim());
            }, 300);
        },
        [doSearch]
    );

    var handleFocus = useCallback(
        function () {
            setShowList(true);
            if (localResults.length === 0) doSearch('');
        },
        [localResults, doSearch]
    );

    // close on click outside
    useEffect(
        function () {
            if (!showList) return;
            var handleClickOutside = function (e) {
                if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowList(false);
            };
            document.addEventListener('mousedown', handleClickOutside);
            return function () {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        },
        [showList]
    );

    var getInitial = function (name) {
        return name ? name.charAt(0).toUpperCase() : '?';
    };

    if (userCode) {
        return (
            <div className="task-assignee-selected">
                <span className="task-assignee-avatar">{getInitial(userName || userCode)}</span>
                <span className="task-assignee-name">{userName || userCode}</span>
                <button
                    className="task-assignee-clear"
                    onClick={function () {
                        onClear();
                        setLocalSearch('');
                        setShowList(false);
                    }}
                >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>
            </div>
        );
    }

    return (
        <div ref={wrapRef}>
            <div className="task-user-search-wrap">
                <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ flexShrink: 0, color: '#8da4c8' }}
                >
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                    className="task-user-search-input"
                    value={localSearch}
                    onChange={handleSearchChange}
                    onFocus={handleFocus}
                    placeholder={AntLocalization.translateText({ vi: 'Tìm theo tên, mã...', en: 'Search by name or code...', fr: 'Rechercher par nom ou code...', es: 'Buscar por nombre o código...', id: 'Cari nama atau kode...', ja: '名前またはコードで検索...', zh: '按名称或编码搜索...', ko: '이름 또는 코드로 검색...', hi: 'नाम या कोड से खोजें...', ar: 'البحث بالاسم أو الرمز...' })}
                />
            </div>
            {showList && (
                <div className="task-user-list">
                    {localLoading && (
                        <div className="task-user-list-msg">{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '불러오는 중...', hi: 'लोड हो रहा है...', ar: 'جارٍ التحميل...' })}</div>
                    )}
                    {!localLoading && localResults.length === 0 && (
                        <div className="task-user-list-msg">{AntLocalization.translateText({ vi: 'Không tìm thấy', en: 'Not found', fr: 'Aucun résultat', es: 'No encontrado', id: 'Tidak ditemukan', ja: '見つかりません', zh: '未找到', ko: '찾을 수 없음', hi: 'नहीं मिला', ar: 'لم يتم العثور' })}</div>
                    )}
                    {localResults.map(function (u) {
                        var uName = u.user_name || u.full_name || u.name || u.user_code || u.code || '';
                        var uCode = u.user_code || u.code || '';
                        return (
                            <div
                                key={u.id || uCode}
                                className="task-user-item"
                                onClick={function () {
                                    onSelect(uCode, uName);
                                    setShowList(false);
                                    setLocalSearch('');
                                }}
                            >
                                <span className="task-user-avatar">{getInitial(uName)}</span>
                                <div className="task-user-info">
                                    <div className="task-user-name">{uName}</div>
                                    {uCode && <div className="task-user-code">{uCode}</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function TaskPanel(props) {
    var context = props.context;
    var onSwitchPanel = props.onSwitchPanel;

    var taskAPI = useMemo(function () {
        return new AntAPI('of', 'OFTask');
    }, []);
    var docAPI = useMemo(function () {
        return new AntAPI('media', 'MEDDocument');
    }, []);
    var [taskDocs, setTaskDocs] = useState([]);
    var [docsLoading, setDocsLoading] = useState(false);
    var [taskViewerState, setTaskViewerState] = useState(null);
    var taskFileRef = useRef(null);

    var [tasks, setTasks] = useState([]);
    var [loading, setLoading] = useState(false);
    var [newTitle, setNewTitle] = useState('');
    var [newDesc, setNewDesc] = useState('');
    var [newTaskType, setNewTaskType] = useState('Task');
    var [newPriority, setNewPriority] = useState('Normal');
    var [newUserCode, setNewUserCode] = useState('');
    var [newUserName, setNewUserName] = useState('');
    var [newSupportCode, setNewSupportCode] = useState('');
    var [newSupportName, setNewSupportName] = useState('');
    var [creating, setCreating] = useState(false);
    var [taskView, setTaskView] = useState('list'); // list | create | detail | settings
    var [activeTask, setActiveTask] = useState(null);
    var [editData, setEditData] = useState({});
    var [saving, setSaving] = useState(false);
    var [isDirty, setIsDirty] = useState(false);
    var [childTasks, setChildTasks] = useState([]);
    var [showComment, setShowComment] = useState(false);
    var [taskComments, setTaskComments] = useState([]);
    var commentAPI = useMemo(function () {
        return new AntAPI('user', 'ACUserComment');
    }, []);

    var currentUserCode = useMemo(function () {
        var session = AntSession ? AntSession() : null;
        return session ? session.user_code : null;
    }, []);

    // ─── Load ───────────────────────────────────────────────────
    var loadTasks = useCallback(
        function () {
            if (!context || !context.voucherType) return;
            setLoading(true);
            taskAPI.post(
                '/' + taskAPI.routeURL + '/list',
                {
                    voucher_type: context.voucherType,
                    voucher_id: context.voucherId,
                    voucher_code: context.voucherCode,
                    pageSize: 30,
                    ordering: [{ name: 'id', type: 'desc' }]
                },
                function (result) {
                    var rows = (result && result.data && result.data.rows) || [];
                    setTasks(rows instanceof Array ? rows : []);
                    setLoading(false);
                }
            );
        },
        [context, taskAPI]
    );

    useEffect(
        function () {
            loadTasks();
        },
        [context ? context.voucherId : null]
    );

    // ─── Create ─────────────────────────────────────────────────
    var openCreateView = useCallback(function () {
        setNewTitle('');
        setNewDesc('');
        setNewTaskType('Task');
        setNewPriority('Normal');
        setNewUserCode('');
        setNewUserName('');
        setNewSupportCode('');
        setNewSupportName('');
        setTaskView('create');
    }, []);

    var handleCreate = useCallback(
        function () {
            var title = newTitle.trim();
            if (!title || !context || !context.voucherType) return;
            setCreating(true);
            var data = {
                type: 'VoucherTask',
                title: title,
                task_type: newTaskType || 'Task',
                priority: newPriority || 'Normal',
                voucher_type: context.voucherType,
                voucher_id: context.voucherId,
                voucher_code: context.voucherCode
            };
            if (newDesc.trim()) data.description = newDesc.trim();
            if (newUserCode) data.user_code = newUserCode;
            if (newSupportCode) data.support1_user_code = newSupportCode;
            taskAPI.post('/' + taskAPI.routeURL + '/create', data, function () {
                setCreating(false);
                setNewTitle('');
                setNewDesc('');
                setNewTaskType('Task');
                setNewPriority('Normal');
                setNewUserCode('');
                setNewUserName('');
                setNewSupportCode('');
                setNewSupportName('');
                setTaskView('list');
                loadTasks();
            });
        },
        [newTitle, newDesc, newTaskType, newPriority, newUserCode, newSupportCode, context, taskAPI, loadTasks]
    );

    var handleCreateKeyDown = useCallback(
        function (e) {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleCreate();
            }
            if (e.key === 'Escape') {
                setTaskView('list');
            }
        },
        [handleCreate]
    );

    // ─── Open detail ────────────────────────────────────────────
    var loadTaskComments = useCallback(
        function (task) {
            var t = task || activeTask;
            if (!t || !t.id) {
                setTaskComments([]);
                return;
            }
            commentAPI.post(
                '/' + commentAPI.routeURL + '/list',
                {
                    voucher_type: 'OFTask',
                    voucher_id: t.id,
                    voucher_code: t.code,
                    pageSize: 10,
                    pageIndex: 0,
                    ordering: [{ name: 'id', type: 'desc' }]
                },
                function (result) {
                    var rows = (result && result.data && result.data.rows) || [];
                    setTaskComments((rows instanceof Array ? rows : []).slice().reverse());
                }
            );
        },
        [activeTask, commentAPI]
    );

    var openTask = useCallback(
        function (task) {
            setActiveTask(task);
            setEditData({
                title: task.title || '',
                description: task.description || '',
                task_type: task.task_type || 'Task',
                priority: task.priority || 'Normal',
                severity: task.severity || 'Normal',
                due_date: task.due_date || '',
                delivery_type: task.delivery_type || 'None',
                delivery_description: task.delivery_description || '',
                user_code: task.user_code || '',
                user_name: task.user_name || '',
                manage_user_code: task.manage_user_code || '',
                support1_user_code: task.support1_user_code || '',
                support1_user_name: task.support1_user_name || ''
            });
            setIsDirty(false);
            setTaskView('detail');
            loadTaskDocs(task);
            loadTaskComments(task);
            taskAPI.post(
                '/' + taskAPI.routeURL + '/list',
                {
                    parent_task_id: task.id,
                    pageSize: 20,
                    ordering: [{ name: 'id', type: 'desc' }]
                },
                function (result) {
                    var rows = (result && result.data && result.data.rows) || [];
                    setChildTasks(rows instanceof Array ? rows : []);
                }
            );
        },
        [taskAPI, loadTaskComments]
    );

    var backToList = useCallback(
        function () {
            setActiveTask(null);
            setTaskView('list');
            setIsDirty(false);
            setShowComment(false);
            loadTasks();
        },
        [loadTasks]
    );

    // ─── Save (settings) ────────────────────────────────────────
    var handleSave = useCallback(
        function () {
            if (!activeTask || !isDirty) return;
            setSaving(true);
            var update = {};
            var ed = editData;
            if (ed.title !== undefined) update.title = (ed.title || '').trim();
            if (ed.description !== undefined) update.description = (ed.description || '').trim();
            if (ed.task_type) update.task_type = ed.task_type;
            if (ed.priority) update.priority = ed.priority;
            if (ed.severity) update.severity = ed.severity;
            if (ed.due_date !== undefined) update.due_date = ed.due_date || null;
            if (ed.delivery_type) update.delivery_type = ed.delivery_type;
            if (ed.delivery_description !== undefined) update.delivery_description = ed.delivery_description;
            if (ed.user_code !== undefined) update.user_code = ed.user_code || null;
            if (ed.manage_user_code !== undefined) update.manage_user_code = ed.manage_user_code || null;
            if (ed.support1_user_code !== undefined) update.support1_user_code = ed.support1_user_code || null;

            taskAPI.post(
                '/' + taskAPI.routeURL + '/update',
                {
                    where: { id: activeTask.id },
                    update: update
                },
                function () {
                    setSaving(false);
                    setIsDirty(false);
                    setActiveTask(function (prev) {
                        return prev ? Object.assign({}, prev, update) : prev;
                    });
                    loadTasks();
                }
            );
        },
        [activeTask, editData, isDirty, taskAPI, loadTasks]
    );

    var updateEditField = useCallback(function (field, value) {
        setEditData(function (prev) {
            var n = Object.assign({}, prev);
            n[field] = value;
            return n;
        });
        setIsDirty(true);
    }, []);

    // ─── Quick actions (status change) ────────────────────────
    var handleQuickAction = useCallback(
        function (actionName) {
            if (!activeTask) return;
            taskAPI.post('/' + taskAPI.routeURL + '/' + actionName, { id: activeTask.id }, function (result) {
                var data = (result && result.data) || result;
                if (data && data.error) {
                    if (typeof window !== 'undefined' && window.alert) window.alert(data.message || data.errorMessage || 'Action failed');
                    return;
                }
                if (data) {
                    setActiveTask(function (prev) {
                        if (!prev) return prev;
                        var updated = Object.assign({}, prev);
                        if (data.task_status) updated.task_status = data.task_status;
                        if (data.started_at) updated.started_at = data.started_at;
                        if (data.completed_at) updated.completed_at = data.completed_at;
                        if (data.delivery_status) updated.delivery_status = data.delivery_status;
                        if (data.progress_pct != null) updated.progress_pct = data.progress_pct;
                        return updated;
                    });
                    loadTasks();
                }
            });
        },
        [activeTask, taskAPI, loadTasks]
    );

    // ─── Delete ─────────────────────────────────────────────────
    var handleDelete = useCallback(
        function () {
            if (!activeTask) return;
            taskAPI.post('/' + taskAPI.routeURL + '/delete', { id: activeTask.id }, function () {
                backToList();
            });
        },
        [activeTask, taskAPI, backToList]
    );

    // ─── Create sub-task ──────────────────────────────────────
    var handleCreateSubTask = useCallback(
        function () {
            if (!activeTask || !context) return;
            var subTitle = window.prompt(AntLocalization.translateText({ vi: 'Tiêu đề task con:', en: 'Sub-task title:', fr: 'Titre sous-tâche :', es: 'Título de la subtarea:', id: 'Judul subtugas:', ja: 'サブタスクのタイトル:', zh: '子任务标题：', ko: '하위 작업 제목:', hi: 'उप-कार्य शीर्षक:', ar: 'عنوان المهمة الفرعية:' }));
            if (!subTitle || !subTitle.trim()) return;
            taskAPI.post(
                '/' + taskAPI.routeURL + '/create',
                {
                    type: 'VoucherTask',
                    title: subTitle.trim(),
                    parent_task_id: activeTask.id,
                    parent_task_code: activeTask.code,
                    voucher_type: context.voucherType,
                    voucher_id: context.voucherId,
                    voucher_code: context.voucherCode
                },
                function () {
                    openTask(activeTask);
                    loadTasks();
                }
            );
        },
        [activeTask, context, taskAPI, openTask, loadTasks]
    );

    // ─── Attachments ──────────────────────────────────────────
    var loadTaskDocs = useCallback(
        function (task) {
            var t = task || activeTask;
            if (!t || !t.id) {
                setTaskDocs([]);
                return;
            }
            setDocsLoading(true);
            docAPI.post('/' + docAPI.routeURL + '/list', { voucher_type: 'OFTask', voucher_id: t.id, pageSize: 50 }, function (result) {
                var rows = (result && result.data && result.data.rows) || [];
                setTaskDocs(rows instanceof Array ? rows : []);
                setDocsLoading(false);
            });
        },
        [activeTask, docAPI]
    );

    var handleTaskFileUpload = useCallback(
        function (e) {
            if (!e.target.files || !e.target.files[0] || !activeTask) return;
            var file = e.target.files[0];
            e.target.value = '';
            var fd = new FormData();
            fd.append('files', file);
            // NB3: Upload route qua media/MEDDocument/upload + voucher context = OFTask
            // BE actionUploadDocuments tạo junction MEDDocumentVoucher (voucher_type='OFTask').
            fd.append('voucher_type', 'OFTask');
            fd.append('voucher_id', activeTask.id);
            if (activeTask.code) fd.append('voucher_code', activeTask.code);
            docAPI.upload('/' + docAPI.routeURL + '/upload', fd, function () {
                loadTaskDocs(activeTask);
            });
        },
        [activeTask, docAPI, loadTaskDocs]
    );

    var handlePreviewDoc = useCallback(
        function (doc) {
            var viewFiles = taskDocs
                .filter(function (x) {
                    return x.full_path || x.url || x.file_url;
                })
                .map(function (x) {
                    return { name: x.name || x.file_name, url: x.full_path || x.url || x.file_url, type: x.sub_type || '' };
                });
            var idx = 0;
            var targetUrl = doc.full_path || doc.url || doc.file_url;
            for (var i = 0; i < viewFiles.length; i++) {
                if (viewFiles[i].url === targetUrl) {
                    idx = i;
                    break;
                }
            }
            if (viewFiles.length > 0) setTaskViewerState({ files: viewFiles, index: idx });
        },
        [taskDocs]
    );

    var handleDeleteTaskDoc = useCallback(
        function (doc) {
            docAPI.post('/' + docAPI.routeURL + '/delete', { id: doc.id }, function () {
                loadTaskDocs(activeTask);
            });
        },
        [activeTask, docAPI, loadTaskDocs]
    );

    // ─── Comment (mở FloatingCommentPanel) ────────────────────────
    var handleOpenComment = useCallback(
        function () {
            if (!activeTask) return;
            setShowComment(true);
        },
        [activeTask]
    );

    // ─── Priority helpers ───────────────────────────────────────
    function getPriorityColor(p) {
        if (p === 'Urgent') return '#b44040';
        if (p === 'High') return '#b8862e';
        if (p === 'Low') return '#94a3b8';
        return null;
    }

    // ─── Empty state ────────────────────────────────────────────
    if (!context || !context.voucherType) {
        return (
            <div className="sidebar-panel-empty">
                <div className="empty-text">
                    {AntLocalization.translateText({ vi: 'Mở chứng từ để xem công việc', en: 'Open a document to see tasks', fr: 'Ouvrez un document pour voir les tâches', es: 'Abra un documento para ver las tareas', id: 'Buka dokumen untuk melihat tugas', ja: '伝票を開くとタスクが表示されます', zh: '打开单据以查看任务', ko: '문서를 열면 작업을 볼 수 있습니다', hi: 'कार्य देखने के लिए दस्तावेज़ खोलें', ar: 'افتح مستندًا لعرض المهام' })}
                </div>
            </div>
        );
    }

    // ═══ CREATE VIEW (màn hình riêng) ═══════════════════════════
    if (taskView === 'create') {
        return (
            <div className="task-panel">
                <div className="task-subview-header">
                    <button
                        className="task-back-btn"
                        onClick={function () {
                            setTaskView('list');
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <span className="task-subview-title">{AntLocalization.translateText({ vi: 'Tạo công việc', en: 'New Task', fr: 'Nouvelle tâche', es: 'Nueva tarea', id: 'Tugas baru', ja: '新規タスク', zh: '新建任务', ko: '새 작업', hi: 'नया कार्य', ar: 'مهمة جديدة' })}</span>
                </div>
                <div className="task-create-body">
                    <textarea
                        className="task-create-title-input"
                        value={newTitle}
                        onChange={function (e) {
                            setNewTitle(e.target.value);
                        }}
                        onKeyDown={handleCreateKeyDown}
                        rows={1}
                        placeholder={AntLocalization.translateText({ vi: 'Tiêu đề công việc...', en: 'Task title...', fr: 'Titre de la tâche...', es: 'Título de tarea...', id: 'Judul tugas...', ja: 'タスクのタイトル...', zh: '任务标题...', ko: '작업 제목...', hi: 'कार्य का शीर्षक...', ar: 'عنوان المهمة...' })}
                        autoFocus
                        disabled={creating}
                    />
                    <textarea
                        className="task-create-desc-input"
                        value={newDesc}
                        onChange={function (e) {
                            setNewDesc(e.target.value);
                        }}
                        onKeyDown={handleCreateKeyDown}
                        rows={2}
                        placeholder={AntLocalization.translateText({ vi: 'Mô tả (tùy chọn)...', en: 'Description (optional)...', fr: 'Description (facultatif)...', es: 'Descripción (opcional)...', id: 'Deskripsi (opsional)...', ja: '説明（任意）...', zh: '描述（可选）...', ko: '설명(선택)...', hi: 'विवरण (वैकल्पिक)...', ar: 'الوصف (اختياري)...' })}
                        disabled={creating}
                    />
                    <div className="task-create-divider" />
                    <div className="task-create-row">
                        <div className="task-create-field">
                            <span className="task-create-field-label">{AntLocalization.translateText({ vi: 'Loại', en: 'Type', fr: 'Type', es: 'Tipo', id: 'Jenis', ja: '種類', zh: '类型', ko: '유형', hi: 'प्रकार', ar: 'النوع' })}</span>
                            <TaskDropdown
                                options={TASK_TYPE_OPTIONS.slice(0, 5)}
                                value={newTaskType}
                                onChange={function (v) {
                                    setNewTaskType(v);
                                }}
                            />
                        </div>
                        <div className="task-create-field">
                            <span className="task-create-field-label">{AntLocalization.translateText({ vi: 'Ưu tiên', en: 'Priority', fr: 'Priorité', es: 'Prioridad', id: 'Prioritas', ja: '優先度', zh: '优先级', ko: '우선순위', hi: 'प्राथमिकता', ar: 'الأولوية' })}</span>
                            <TaskDropdown
                                options={PRIORITY_OPTIONS}
                                value={newPriority}
                                onChange={function (v) {
                                    setNewPriority(v);
                                }}
                            />
                        </div>
                    </div>
                    <div className="task-create-divider" />
                    {/* ── Người được giao ─────────────────────────── */}
                    <div className="task-create-field">
                        <span className="task-create-field-label">{AntLocalization.translateText({ vi: 'Người được giao', en: 'Assignee', fr: 'Responsable', es: 'Responsable', id: 'Penerima tugas', ja: '担当者', zh: '负责人', ko: '담당자', hi: 'कार्यकर्ता', ar: 'المكلَّف' })}</span>
                        <TaskUserField
                            userCode={newUserCode}
                            userName={newUserName}
                            onSelect={function (code, name) {
                                setNewUserCode(code);
                                setNewUserName(name);
                            }}
                            onClear={function () {
                                setNewUserCode('');
                                setNewUserName('');
                            }}
                        />
                    </div>
                    {/* ── Người hỗ trợ ───────────────────────────── */}
                    <div className="task-create-field">
                        <span className="task-create-field-label">{AntLocalization.translateText({ vi: 'Người hỗ trợ', en: 'Support', fr: 'Soutien', es: 'Apoyo', id: 'Pendukung', ja: 'サポート担当', zh: '协助人', ko: '지원자', hi: 'सहयोगी', ar: 'الداعم' })}</span>
                        <TaskUserField
                            userCode={newSupportCode}
                            userName={newSupportName}
                            onSelect={function (code, name) {
                                setNewSupportCode(code);
                                setNewSupportName(name);
                            }}
                            onClear={function () {
                                setNewSupportCode('');
                                setNewSupportName('');
                            }}
                        />
                    </div>
                </div>
                <div className="task-create-footer">
                    <button
                        className="task-create-cancel"
                        onClick={function () {
                            setTaskView('list');
                        }}
                    >
                        {AntLocalization.translateText({ vi: 'Hủy', en: 'Cancel', fr: 'Annuler', es: 'Cancelar', id: 'Batal', ja: 'キャンセル', zh: '取消', ko: '취소', hi: 'रद्द करें', ar: 'إلغاء' })}
                    </button>
                    <button className="task-create-submit" onClick={handleCreate} disabled={!newTitle.trim() || creating}>
                        {creating
                            ? AntLocalization.translateText({ vi: 'Đang tạo...', en: 'Creating...', fr: 'Création...', es: 'Creando...', id: 'Membuat...', ja: '作成中...', zh: '创建中...', ko: '만드는 중...', hi: 'बनाया जा रहा है...', ar: 'جارٍ الإنشاء...' })
                            : AntLocalization.translateText({ vi: 'Tạo công việc', en: 'Create Task', fr: 'Créer la tâche', es: 'Crear tarea', id: 'Buat tugas', ja: 'タスクを作成', zh: '创建任务', ko: '작업 만들기', hi: 'कार्य बनाएँ', ar: 'إنشاء مهمة' })}
                    </button>
                </div>
            </div>
        );
    }

    // ═══ SETTINGS VIEW ═════════════════════════════════════════
    if (taskView === 'settings' && activeTask) {
        var canManageEdit =
            activeTask.request_user_code === currentUserCode ||
            activeTask.manage_user_code === currentUserCode ||
            (!activeTask.manage_user_code && activeTask.user_code === currentUserCode);
        var ed = editData;
        var isBugEdit = ed.task_type === 'Bug' || ed.task_type === 'Issue';
        return (
            <div className="task-panel">
                <div className="task-subview-header">
                    <button
                        className="task-back-btn"
                        onClick={function () {
                            if (isDirty) {
                                handleSave();
                            }
                            setTaskView('detail');
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <span className="task-subview-title">{AntLocalization.translateText({ vi: 'Chỉnh sửa', en: 'Edit', fr: 'Modifier', es: 'Editar', id: 'Ubah', ja: '編集', zh: '编辑', ko: '편집', hi: 'संपादित करें', ar: 'تعديل' })}</span>
                    {isDirty && (
                        <button className="task-save-btn" onClick={handleSave} disabled={saving}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M5 13l4 4L19 7" />
                            </svg>
                        </button>
                    )}
                </div>
                <div className="task-settings-body">
                    <div className="task-settings-section">
                        <span className="task-settings-label">{AntLocalization.translateText({ vi: 'Tiêu đề', en: 'Title', fr: 'Titre', es: 'Título', id: 'Judul', ja: 'タイトル', zh: '标题', ko: '제목', hi: 'शीर्षक', ar: 'العنوان' })}</span>
                        <input
                            className="task-settings-input"
                            value={ed.title || ''}
                            onChange={function (e) {
                                updateEditField('title', e.target.value);
                            }}
                        />
                    </div>
                    <div className="task-settings-section">
                        <span className="task-settings-label">{AntLocalization.translateText({ vi: 'Mô tả', en: 'Description', fr: 'Description', es: 'Descripción', id: 'Deskripsi', ja: '説明', zh: '描述', ko: '설명', hi: 'विवरण', ar: 'الوصف' })}</span>
                        <textarea
                            className="task-settings-input"
                            value={ed.description || ''}
                            rows={3}
                            onChange={function (e) {
                                updateEditField('description', e.target.value);
                            }}
                        />
                    </div>
                    <div className="task-create-row">
                        <div className="task-settings-section">
                            <span className="task-settings-label">{AntLocalization.translateText({ vi: 'Loại', en: 'Type', fr: 'Type', es: 'Tipo', id: 'Jenis', ja: '種類', zh: '类型', ko: '유형', hi: 'प्रकार', ar: 'النوع' })}</span>
                            <TaskDropdown
                                options={TASK_TYPE_OPTIONS}
                                value={ed.task_type}
                                onChange={function (v) {
                                    updateEditField('task_type', v);
                                }}
                            />
                        </div>
                        <div className="task-settings-section">
                            <span className="task-settings-label">{AntLocalization.translateText({ vi: 'Ưu tiên', en: 'Priority', fr: 'Priorité', es: 'Prioridad', id: 'Prioritas', ja: '優先度', zh: '优先级', ko: '우선순위', hi: 'प्राथमिकता', ar: 'الأولوية' })}</span>
                            <TaskDropdown
                                options={PRIORITY_OPTIONS}
                                value={ed.priority}
                                onChange={function (v) {
                                    updateEditField('priority', v);
                                }}
                            />
                        </div>
                    </div>
                    {isBugEdit && (
                        <div className="task-settings-section">
                            <span className="task-settings-label">{AntLocalization.translateText({ vi: 'Mức độ', en: 'Severity', fr: 'Gravité', es: 'Gravedad', id: 'Keparahan', ja: '重大度', zh: '严重程度', ko: '심각도', hi: 'गंभीरता', ar: 'الخطورة' })}</span>
                            <TaskDropdown
                                options={SEVERITY_OPTIONS}
                                value={ed.severity}
                                onChange={function (v) {
                                    updateEditField('severity', v);
                                }}
                            />
                        </div>
                    )}
                    <div className="task-settings-section">
                        <span className="task-settings-label">{AntLocalization.translateText({ vi: 'Kết quả cần giao', en: 'Deliverable', fr: 'Livrable', es: 'Entregable', id: 'Hasil kerja', ja: '成果物', zh: '交付成果', ko: '산출물', hi: 'डिलिवरेबल', ar: 'المخرجات' })}</span>
                        <TaskDropdown
                            options={DELIVERY_OPTIONS}
                            value={ed.delivery_type}
                            onChange={function (v) {
                                updateEditField('delivery_type', v);
                            }}
                        />
                        {ed.delivery_type && ed.delivery_type !== 'None' && (
                            <textarea
                                className="task-settings-input"
                                style={{ marginTop: 6 }}
                                value={ed.delivery_description || ''}
                                rows={2}
                                onChange={function (e) {
                                    updateEditField('delivery_description', e.target.value);
                                }}
                                placeholder={AntLocalization.translateText({ vi: 'Mô tả kết quả cần nộp...', en: 'Describe deliverables...', fr: 'Décrire les livrables...', es: 'Describa los entregables...', id: 'Jelaskan hasil kerja...', ja: '成果物を記述...', zh: '描述交付成果...', ko: '산출물 설명...', hi: 'डिलिवरेबल का वर्णन करें...', ar: 'صف المخرجات...' })}
                            />
                        )}
                    </div>
                    {/* ── Người được giao ─────────────────────────── */}
                    <div className="task-settings-section">
                        <span className="task-settings-label">{AntLocalization.translateText({ vi: 'Người được giao', en: 'Assignee', fr: 'Responsable', es: 'Responsable', id: 'Penerima tugas', ja: '担当者', zh: '负责人', ko: '담당자', hi: 'कार्यकर्ता', ar: 'المكلَّف' })}</span>
                        <TaskUserField
                            userCode={ed.user_code}
                            userName={ed.user_name || (activeTask && activeTask.user_name) || ''}
                            onSelect={function (code, name) {
                                updateEditField('user_code', code);
                                updateEditField('user_name', name);
                            }}
                            onClear={function () {
                                updateEditField('user_code', '');
                                updateEditField('user_name', '');
                            }}
                        />
                    </div>
                    {/* ── Người hỗ trợ ───────────────────────────── */}
                    <div className="task-settings-section">
                        <span className="task-settings-label">{AntLocalization.translateText({ vi: 'Người hỗ trợ', en: 'Support', fr: 'Soutien', es: 'Apoyo', id: 'Pendukung', ja: 'サポート担当', zh: '协助人', ko: '지원자', hi: 'सहयोगी', ar: 'الداعم' })}</span>
                        <TaskUserField
                            userCode={ed.support1_user_code}
                            userName={ed.support1_user_name || (activeTask && activeTask.support1_user_name) || ''}
                            onSelect={function (code, name) {
                                updateEditField('support1_user_code', code);
                                updateEditField('support1_user_name', name);
                            }}
                            onClear={function () {
                                updateEditField('support1_user_code', '');
                                updateEditField('support1_user_name', '');
                            }}
                        />
                    </div>
                    <div className="task-settings-section">
                        <AntAttachmentSection
                            docs={taskDocs}
                            loading={docsLoading}
                            onUpload={handleTaskFileUpload}
                            onDelete={handleDeleteTaskDoc}
                            onPreview={handlePreviewDoc}
                            fileInputRef={taskFileRef}
                        />
                    </div>
                    {canManageEdit && (
                        <div className="task-settings-danger">
                            <button className="task-settings-delete" onClick={handleDelete}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                {AntLocalization.translateText({ vi: 'Xoá công việc', en: 'Delete task', fr: 'Supprimer la tâche', es: 'Eliminar tarea', id: 'Hapus tugas', ja: 'タスクを削除', zh: '删除任务', ko: '작업 삭제', hi: 'कार्य हटाएँ', ar: 'حذف المهمة' })}
                            </button>
                        </div>
                    )}
                </div>
                {taskViewerState && (
                    <AntFileViewer
                        visible
                        files={taskViewerState.files}
                        initialIndex={taskViewerState.index}
                        onClose={function () {
                            setTaskViewerState(null);
                        }}
                    />
                )}
            </div>
        );
    }

    // ═══ DETAIL VIEW ══════════════════════════════════════════
    if (taskView === 'detail' && activeTask) {
        var sc2 = getStatusColor(activeTask.task_status);
        var canEdit2 =
            activeTask.request_user_code === currentUserCode ||
            activeTask.manage_user_code === currentUserCode ||
            (!activeTask.manage_user_code && activeTask.user_code === currentUserCode);
        var isAssignee2 = activeTask.user_code === currentUserCode;
        var tt2 = activeTask.task_type || 'Task';
        var isOverdue2 = activeTask.due_date && ['Close', 'Done', 'Cancel'].indexOf(activeTask.task_status) < 0;
        var priColor2 = getPriorityColor(activeTask.priority);

        // Main action — chỉ 1 nút chính theo status
        var mainAction = null;
        if (activeTask.task_status === 'New' && canEdit2)
            mainAction = { name: 'actionRelease', label: AntLocalization.translateText({ vi: 'Giao việc', en: 'Assign', fr: 'Attribuer', es: 'Asignar', id: 'Tugaskan', ja: '割り当て', zh: '分配', ko: '배정', hi: 'सौंपें', ar: 'تعيين' }), color: '#5b8fd9' };
        if (activeTask.task_status === 'ToDo' && (isAssignee2 || canEdit2))
            mainAction = {
                name: 'actionStart',
                label: AntLocalization.translateText({ vi: 'Bắt đầu thực hiện', en: 'Start Working', fr: 'Commencer', es: 'Comenzar', id: 'Mulai kerjakan', ja: '作業開始', zh: '开始执行', ko: '작업 시작', hi: 'काम शुरू करें', ar: 'بدء العمل' }),
                color: '#5b8fd9'
            };
        if (activeTask.task_status === 'WIP' && (isAssignee2 || canEdit2))
            mainAction = { name: 'actionDone', label: AntLocalization.translateText({ vi: 'Hoàn tất', en: 'Mark as Done', fr: 'Terminer', es: 'Completar', id: 'Tandai selesai', ja: '完了にする', zh: '标记为完成', ko: '완료로 표시', hi: 'पूर्ण करें', ar: 'تعليم كمكتمل' }), color: '#3da06a' };
        if ((activeTask.task_status === 'Done' || activeTask.task_status === 'Failed') && canEdit2)
            mainAction = { name: 'actionClose', label: AntLocalization.translateText({ vi: 'Đóng công việc', en: 'Close Task', fr: 'Clôturer la tâche', es: 'Cerrar tarea', id: 'Tutup tugas', ja: 'タスクを閉じる', zh: '关闭任务', ko: '작업 종료', hi: 'कार्य बंद करें', ar: 'إغلاق المهمة' }), color: '#64748b' };

        // Info rows
        var infoRows = [];
        if (activeTask.user_name)
            infoRows.push({
                label: AntLocalization.translateText({ vi: 'Thực hiện', en: 'Assignee', fr: 'Responsable', es: 'Responsable', id: 'Pelaksana', ja: '担当者', zh: '执行人', ko: '담당자', hi: 'कार्यकर्ता', ar: 'المنفذ' }),
                value: activeTask.entity_full_name || activeTask.entity_name || activeTask.user_name
            });
        if (activeTask.manage_user_name)
            infoRows.push({
                label: AntLocalization.translateText({ vi: 'Quản lý', en: 'Manager', fr: 'Responsable', es: 'Responsable', id: 'Manajer', ja: '管理者', zh: '负责人', ko: '관리자', hi: 'प्रबंधक', ar: 'المدير' }),
                value: activeTask.manage_entity_full_name || activeTask.manage_entity_name || activeTask.manage_user_name
            });
        if (activeTask.support1_user_name)
            infoRows.push({
                label: AntLocalization.translateText({ vi: 'Hỗ trợ', en: 'Support', fr: 'Soutien', es: 'Apoyo', id: 'Pendukung', ja: 'サポート', zh: '协助', ko: '지원', hi: 'सहयोग', ar: 'الدعم' }),
                value: activeTask.support1_entity_full_name || activeTask.support1_entity_name || activeTask.support1_user_name
            });
        if (activeTask.user_group_name)
            infoRows.push({ label: AntLocalization.translateText({ vi: 'Phòng ban', en: 'Department', fr: 'Service', es: 'Departamento', id: 'Departemen', ja: '部署', zh: '部门', ko: '부서', hi: 'विभाग', ar: 'القسم' }), value: activeTask.user_group_name });
        if (activeTask.due_date)
            infoRows.push({
                label: AntLocalization.translateText({ vi: 'Hạn', en: 'Due', fr: 'Échéance', es: 'Vencimiento', id: 'Tenggat', ja: '期限', zh: '截止', ko: '마감', hi: 'नियत तिथि', ar: 'الاستحقاق' }),
                value: commentFormatTime(activeTask.due_date),
                color: isOverdue2 ? '#dc2626' : null
            });
        if (activeTask.severity && activeTask.severity !== 'Normal' && (tt2 === 'Bug' || tt2 === 'Issue'))
            infoRows.push({
                label: AntLocalization.translateText({ vi: 'Mức độ', en: 'Severity', fr: 'Gravité', es: 'Gravedad', id: 'Keparahan', ja: '重大度', zh: '严重程度', ko: '심각도', hi: 'गंभीरता', ar: 'الخطورة' }),
                value: AntLocalization.translateText(
                    SEVERITY_OPTIONS.find(function (o) {
                        return o.code === activeTask.severity;
                    })?.label || { vi: activeTask.severity, en: activeTask.severity }
                ),
                color: activeTask.severity === 'Critical' || activeTask.severity === 'Blocker' ? '#dc2626' : '#d97706'
            });
        if (activeTask.ref_entity_name)
            infoRows.push({ label: AntLocalization.translateText({ vi: 'Liên quan', en: 'Related', fr: 'Lié', es: 'Relacionado', id: 'Terkait', ja: '関連', zh: '相关', ko: '관련', hi: 'संबंधित', ar: 'ذو صلة' }), value: activeTask.ref_entity_name });
        if (activeTask.store_name)
            infoRows.push({ label: AntLocalization.translateText({ vi: 'Địa điểm', en: 'Location', fr: 'Lieu', es: 'Ubicación', id: 'Lokasi', ja: '場所', zh: '地点', ko: '장소', hi: 'स्थान', ar: 'الموقع' }), value: activeTask.store_name });
        if (activeTask.request_user_name)
            infoRows.push({
                label: AntLocalization.translateText({ vi: 'Tạo bởi', en: 'Created by', fr: 'Créé par', es: 'Creado por', id: 'Dibuat oleh', ja: '作成者', zh: '创建人', ko: '작성자', hi: 'द्वारा बनाया', ar: 'أنشأه' }),
                value: activeTask.request_entity_full_name || activeTask.request_entity_name || activeTask.request_user_name
            });

        return (
            <div className="task-panel">
                {/* Header */}
                <div className="task-subview-header">
                    <button className="task-back-btn" onClick={backToList}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <span className="task-subview-title">{activeTask.title}</span>
                    {canEdit2 && (
                        <button
                            className="task-gear-btn"
                            onClick={function () {
                                setTaskView('settings');
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                        </button>
                    )}
                </div>

                <div className="task-detail-body">
                    {/* ── Zone 1: Status + Type + Priority badges ────── */}
                    <div className="task-detail-zone zone-status">
                        <span className="task-detail-status-badge" style={{ color: sc2, background: sc2 + '14', border: '1px solid ' + sc2 + '30' }}>
                            {getStatusLabel(activeTask.task_status)}
                        </span>
                        {tt2 !== 'Task' && <span className="task-detail-type-badge">{getTaskTypeLabel(tt2)}</span>}
                        {activeTask.priority && activeTask.priority !== 'Normal' && (
                            <span
                                className="task-detail-priority-badge"
                                style={{ color: priColor2 || '#64748b', background: (priColor2 || '#64748b') + '14' }}
                            >
                                {AntLocalization.translateText(
                                    PRIORITY_OPTIONS.find(function (o) {
                                        return o.code === activeTask.priority;
                                    })?.label || { vi: activeTask.priority, en: activeTask.priority }
                                )}
                            </span>
                        )}
                    </div>

                    {/* ── Zone 2: Info rows ───────────────────────────── */}
                    {infoRows.length > 0 && (
                        <div className="task-detail-zone">
                            <div className="task-info-grid">
                                {infoRows.map(function (row, ri) {
                                    return (
                                        <div key={ri} className="task-info-row">
                                            <span className="task-info-label">{row.label}</span>
                                            <span className="task-info-value" style={row.color ? { color: row.color } : null}>
                                                {row.value}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ── Zone 3: Progress ────────────────────────────── */}
                    {activeTask.progress_pct > 0 && (
                        <div className="task-detail-zone">
                            <div className="task-progress-label">
                                <span>{AntLocalization.translateText({ vi: 'Tiến độ', en: 'Progress', fr: 'Avancement', es: 'Progreso', id: 'Progres', ja: '進捗', zh: '进度', ko: '진행률', hi: 'प्रगति', ar: 'التقدم' })}</span>
                                <span>{Math.round(activeTask.progress_pct)}%</span>
                            </div>
                            <div className="task-progress-bar">
                                <div
                                    className="task-progress-fill"
                                    style={{
                                        width: activeTask.progress_pct + '%',
                                        background: activeTask.progress_pct >= 100 ? '#22c55e' : '#3b82f6'
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* ── Zone 4: Description ────────────────────────── */}
                    {activeTask.description && (
                        <div className="task-detail-zone">
                            <span className="task-zone-title">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 6h16M4 12h10M4 18h14" />
                                </svg>
                                {AntLocalization.translateText({ vi: 'Mô tả', en: 'Description', fr: 'Description', es: 'Descripción', id: 'Deskripsi', ja: '説明', zh: '描述', ko: '설명', hi: 'विवरण', ar: 'الوصف' })}
                            </span>
                            <div className="task-detail-desc">{activeTask.description}</div>
                        </div>
                    )}

                    {/* ── Zone 5: Main action ────────────────────────── */}
                    {mainAction && (
                        <div className="task-detail-zone task-action-row">
                            <button
                                className="task-main-action"
                                style={{ background: mainAction.color }}
                                onClick={function () {
                                    handleQuickAction(mainAction.name);
                                }}
                            >
                                {mainAction.label}
                            </button>
                            {activeTask.task_status === 'WIP' && (isAssignee2 || canEdit2) && (
                                <button
                                    className="task-secondary-action"
                                    style={{ color: '#9e5050', borderColor: '#d4b0b0' }}
                                    onClick={function () {
                                        handleQuickAction('actionFailed');
                                    }}
                                >
                                    {AntLocalization.translateText({ vi: 'Báo thất bại', en: 'Report Failed', fr: 'Signaler un échec', es: 'Reportar fallo', id: 'Laporkan gagal', ja: '失敗を報告', zh: '报告失败', ko: '실패 보고', hi: 'विफलता दर्ज करें', ar: 'الإبلاغ عن فشل' })}
                                </button>
                            )}
                        </div>
                    )}

                    {/* ── Zone: Bình luận (read-only flat) ────────────── */}
                    {taskComments.length > 0 && (
                        <div className="task-detail-zone">
                            <div className="task-zone-title" style={{ cursor: 'pointer' }} onClick={handleOpenComment}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                </svg>
                                {AntLocalization.translateText({ vi: 'Bình luận', en: 'Comments', fr: 'Commentaires', es: 'Comentarios', id: 'Komentar', ja: 'コメント', zh: '评论', ko: '댓글', hi: 'टिप्पणियाँ', ar: 'التعليقات' })}
                                <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 2 }}>({taskComments.length})</span>
                            </div>
                            {taskComments.map(function (cm) {
                                var cmName = cm.entity_full_name || cm.entity_name || cm.user_name || '';
                                return (
                                    <div key={cm.id} className="task-inline-comment">
                                        <div className="task-inline-comment-avatar">
                                            {cmName ? cmName.charAt(cmName.trim().lastIndexOf(' ') + 1 || 0).toUpperCase() : '?'}
                                        </div>
                                        <div className="task-inline-comment-body">
                                            <div className="task-inline-comment-header">
                                                <span className="task-inline-comment-author">{cmName}</span>
                                                <span className="task-inline-comment-time">{commentFormatTime(cm.created_at)}</span>
                                            </div>
                                            {cm.description && <div className="task-inline-comment-text">{cm.description}</div>}
                                            {cm.meddoc_name && <div className="task-inline-comment-file"><SvgIcon d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" size={12} /> {cm.meddoc_name}</div>}
                                        </div>
                                    </div>
                                );
                            })}
                            <button className="task-inline-comment-more" onClick={handleOpenComment}>
                                {AntLocalization.translateText({ vi: 'Xem tất cả & trả lời...', en: 'View all & reply...', fr: 'Tout voir et répondre...', es: 'Ver todo y responder...', id: 'Lihat semua & balas...', ja: 'すべて表示して返信...', zh: '查看全部并回复...', ko: '모두 보기 및 답글...', hi: 'सभी देखें और उत्तर दें...', ar: 'عرض الكل والرد...' })}
                            </button>
                        </div>
                    )}

                    {/* ── Zone 6: Checklist ───────────────────────────── */}
                    {activeTask.checklist && activeTask.checklist instanceof Array && activeTask.checklist.length > 0 && (
                        <div className="task-detail-zone">
                            <span className="task-zone-title">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                </svg>
                                {AntLocalization.translateText({ vi: 'Danh sách kiểm tra', en: 'Checklist', fr: 'Checklist', es: 'Lista de control', id: 'Daftar periksa', ja: 'チェックリスト', zh: '检查清单', ko: '체크리스트', hi: 'चेकलिस्ट', ar: 'قائمة التحقق' })}
                            </span>
                            {activeTask.checklist.map(function (cl, ci) {
                                return (
                                    <div key={ci} className="task-checklist-item">
                                        <span className={'task-checklist-check' + (cl.checked ? ' checked' : '')}>
                                            {cl.checked && (
                                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                    <path d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </span>
                                        <span>{cl.text}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* ── Zone 7: Attachments ────────────────────────── */}
                    {taskDocs.length > 0 && (
                        <div className="task-detail-zone">
                            <span className="task-zone-title">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                                {AntLocalization.translateText({ vi: 'Đính kèm', en: 'Attachments', fr: 'Pièces jointes', es: 'Adjuntos', id: 'Lampiran', ja: '添付', zh: '附件', ko: '첨부', hi: 'संलग्नक', ar: 'المرفقات' })}
                            </span>
                            <AntAttachmentSection docs={taskDocs} readOnly onPreview={handlePreviewDoc} />
                        </div>
                    )}

                    {/* ── Zone 8: Parent + children ───────────────────── */}
                    {(activeTask.parent_task_code || childTasks.length > 0) && (
                        <div className="task-detail-zone">
                            {activeTask.parent_task_code && (
                                <div className="task-info-row">
                                    <span className="task-info-label">{AntLocalization.translateText({ vi: 'Thuộc', en: 'Parent', fr: 'Parent', es: 'Principal', id: 'Induk', ja: '親タスク', zh: '父任务', ko: '상위', hi: 'मूल', ar: 'الأصل' })}</span>
                                    <span className="task-info-value" style={{ color: 'var(--AnteBaseColor1, #4f7cff)', cursor: 'pointer' }}>
                                        {activeTask.parent_task_title || activeTask.parent_task_code}
                                    </span>
                                </div>
                            )}
                            {childTasks.length > 0 && (
                                <Fragment>
                                    <span className="task-zone-title" style={{ marginTop: activeTask.parent_task_code ? 8 : 0 }}>
                                        {AntLocalization.translateText({ vi: 'Công việc con', en: 'Sub-tasks', fr: 'Sous-tâches', es: 'Subtareas', id: 'Subtugas', ja: 'サブタスク', zh: '子任务', ko: '하위 작업', hi: 'उप-कार्य', ar: 'المهام الفرعية' })} ({childTasks.length})
                                    </span>
                                    {childTasks.map(function (ct) {
                                        var csc = getStatusColor(ct.task_status);
                                        var ctAssignee = ct.entity_name || ct.user_name || '';
                                        return (
                                            <div
                                                key={ct.id}
                                                className="task-child-item"
                                                onClick={function () {
                                                    openTask(ct);
                                                }}
                                            >
                                                <span className="task-child-dot" style={{ background: csc }} />
                                                <div className="task-child-content">
                                                    <span className="task-child-title">{ct.title}</span>
                                                    <span className="task-child-meta">
                                                        <span style={{ color: csc }}>{getStatusLabel(ct.task_status)}</span>
                                                        {ctAssignee && <span className="task-child-assignee">{ctAssignee}</span>}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Fragment>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Bottom toolbar ──────────────────────────────── */}
                <div className="task-detail-toolbar">
                    {canEdit2 && (
                        <button className="task-flat-btn" onClick={handleCreateSubTask}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                            <span>{AntLocalization.translateText({ vi: 'Thêm việc con', en: 'Sub-task', fr: 'Sous-tâche', es: 'Subtarea', id: 'Subtugas', ja: 'サブタスク', zh: '子任务', ko: '하위 작업', hi: 'उप-कार्य', ar: 'مهمة فرعية' })}</span>
                        </button>
                    )}
                    <button className="task-flat-btn" onClick={handleOpenComment}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                        </svg>
                        <span>{AntLocalization.translateText({ vi: 'Bình luận', en: 'Comment', fr: 'Commenter', es: 'Comentar', id: 'Komentar', ja: 'コメント', zh: '评论', ko: '댓글', hi: 'टिप्पणी', ar: 'تعليق' })}</span>
                    </button>
                </div>

                {taskViewerState && (
                    <AntFileViewer
                        visible
                        files={taskViewerState.files}
                        initialIndex={taskViewerState.index}
                        onClose={function () {
                            setTaskViewerState(null);
                        }}
                    />
                )}
                <FloatingCommentPanel
                    visible={showComment}
                    onClose={function () {
                        setShowComment(false);
                    }}
                    context={
                        showComment && activeTask
                            ? { voucherType: 'OFTask', voucherId: activeTask.id, voucherCode: activeTask.code, voucherTypeLabel: activeTask.title }
                            : null
                    }
                    title={activeTask ? activeTask.title : ''}
                />
            </div>
        );
    }

    // ═══ LIST VIEW ══════════════════════════════════════════════
    return (
        <div className="task-panel">
            <div className="task-list-header">
                <span className="task-list-count">
                    {!loading && tasks.length > 0 && tasks.length + ' ' + AntLocalization.translateText({ vi: 'công việc', en: 'tasks', fr: 'tâches', es: 'tareas', id: 'tugas', ja: '件のタスク', zh: '个任务', ko: '개 작업', hi: 'कार्य', ar: 'مهام' })}
                </span>
                <button className="task-list-add-btn" onClick={openCreateView}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14" />
                    </svg>
                    {AntLocalization.translateText({ vi: 'Tạo mới', en: 'New', fr: 'Nouveau', es: 'Nuevo', id: 'Baru', ja: '新規作成', zh: '新建', ko: '새로 만들기', hi: 'नया', ar: 'جديد' })}
                </button>
            </div>
            <div className="task-list">
                {loading && (
                    <div className="task-loading">
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            style={{ animation: 'spin 1s linear infinite' }}
                        >
                            <path d="M12 2v4m0 12v4m-7.07-3.93l2.83-2.83m8.48-8.48l2.83-2.83M2 12h4m12 0h4m-3.93 7.07l-2.83-2.83M7.76 7.76L4.93 4.93" />
                        </svg>
                        {AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '불러오는 중...', hi: 'लोड हो रहा है...', ar: 'جارٍ التحميل...' })}
                    </div>
                )}
                {!loading && tasks.length === 0 && (
                    <div className="task-empty">
                        <div className="task-empty-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                        </div>
                        <span>{AntLocalization.translateText({ vi: 'Chưa có công việc', en: 'No tasks yet', fr: 'Aucune tâche', es: 'Sin tareas', id: 'Belum ada tugas', ja: 'タスクはありません', zh: '暂无任务', ko: '작업이 없습니다', hi: 'अभी कोई कार्य नहीं', ar: 'لا توجد مهام' })}</span>
                    </div>
                )}
                {tasks.map(function (t) {
                    var sc = getStatusColor(t.task_status);
                    return (
                        <div
                            key={t.id}
                            className="task-item"
                            onClick={function () {
                                openTask(t);
                            }}
                        >
                            <div className="task-status-dot" style={{ backgroundColor: sc, color: sc }} />
                            <div className="task-content">
                                <div className="task-title">{t.title}</div>
                                <div className="task-meta">
                                    <span className="task-status-label" style={{ color: sc }}>
                                        {getStatusLabel(t.task_status)}
                                    </span>
                                    {t.user_name && <span className="task-assignee">{t.entity_name || t.user_name}</span>}
                                </div>
                            </div>
                            <span className="task-item-arrow">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M9 5l7 7-7 7" />
                                </svg>
                            </span>
                        </div>
                    );
                })}
            </div>
            <button className="task-fab" onClick={openCreateView} title={AntLocalization.translateText({ vi: 'Tạo công việc', en: 'New task', fr: 'Nouvelle tâche', es: 'Nueva tarea', id: 'Tugas baru', ja: '新規タスク', zh: '新建任务', ko: '새 작업', hi: 'नया कार्य', ar: 'مهمة جديدة' })}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 5v14M5 12h14" />
                </svg>
            </button>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── AI PANEL ────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

function getMockMessages(context) {
    var voucherCode = (context && context.voucherCode) || 'ARO-2024-0042';
    var voucherLabel = (context && context.voucherTypeLabel) || 'Đơn hàng';
    return [
        {
            role: 'assistant',
            blocks: [
                { type: 'text', text: 'Xin chào, tôi có thể giúp bạn xử lý ' + voucherLabel + ' ' + voucherCode + '. Hãy cho tôi biết bạn cần gì.' }
            ]
        },
        { role: 'user', blocks: [{ type: 'text', text: 'Cho tôi xem tóm tắt đơn hàng' }] },
        {
            role: 'assistant',
            blocks: [
                {
                    type: 'table',
                    headers: ['Thông tin', 'Giá trị'],
                    rows: [
                        ['Mã đơn', voucherCode],
                        ['Khách hàng', 'Công ty TNHH Minh Phát'],
                        ['Trạng thái', 'Release'],
                        ['Tổng tiền', '15,250,000 đ'],
                        ['Số dòng', '5 sản phẩm'],
                        ['Ngày tạo', '20/03/2026']
                    ]
                },
                { type: 'text', text: 'Đơn hàng đang ở trạng thái Release, đã sẵn sàng để xuất kho.' }
            ]
        },
        { role: 'user', blocks: [{ type: 'text', text: 'Xuất kho cho đơn này' }] },
        {
            role: 'assistant',
            blocks: [
                { type: 'text', text: 'Xác nhận thực hiện xuất kho cho đơn hàng ' + voucherCode + '?' },
                {
                    type: 'action',
                    actions: [
                        { key: 'confirm', label: 'Xác nhận xuất kho', variant: 'primary' },
                        { key: 'cancel', label: 'Hủy', variant: 'default' }
                    ]
                }
            ]
        }
    ];
}

function MessageBlock(props) {
    var block = props.block;
    var onAction = props.onAction;
    if (block.type === 'text') return <div className="ai-msg-text">{block.text}</div>;
    if (block.type === 'table') {
        return (
            <div className="ai-msg-table-wrap">
                <table className="ai-msg-table">
                    {block.headers && (
                        <thead>
                            <tr>
                                {block.headers.map(function (h, i) {
                                    return <th key={i}>{h}</th>;
                                })}
                            </tr>
                        </thead>
                    )}
                    <tbody>
                        {block.rows.map(function (row, ri) {
                            return (
                                <tr key={ri}>
                                    {row.map(function (cell, ci) {
                                        return <td key={ci}>{cell}</td>;
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }
    if (block.type === 'action') {
        return (
            <div className="ai-msg-actions">
                {block.actions.map(function (a) {
                    return (
                        <button
                            key={a.key}
                            className={'ai-action-btn ai-action-' + (a.variant || 'default')}
                            onClick={function () {
                                if (onAction) onAction(a.key);
                            }}
                        >
                            {a.label}
                        </button>
                    );
                })}
            </div>
        );
    }
    if (block.type === 'status') {
        return (
            <div className={'ai-msg-status ai-status-' + (block.variant || 'info')}>
                {block.variant === 'success' && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                )}
                {block.variant === 'error' && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                )}
                {block.variant === 'info' && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                )}
                <span>{block.text}</span>
            </div>
        );
    }
    return null;
}

function AIPanel(props) {
    var context = props.context;
    var [messages, setMessages] = useState([]);
    var [inputText, setInputText] = useState('');
    var [isTyping, setIsTyping] = useState(false);
    var [streamingText, setStreamingText] = useState('');
    var [isStreaming, setIsStreaming] = useState(false);
    var [aiView, setAiView] = useState('chat'); // chat | settings
    var [editConvName, setEditConvName] = useState('');
    var [savingName, setSavingName] = useState(false);
    var [pendingFiles, setPendingFiles] = useState([]);
    var [sending, setSending] = useState(false);
    var [aiDocs, setAiDocs] = useState([]);
    var [aiDocsLoading, setAiDocsLoading] = useState(false);
    var [aiViewerState, setAiViewerState] = useState(null);
    var [conversationId, setConversationId] = useState(null);
    var [dragOver, setDragOver] = useState(false);
    var listRef = useRef(null);
    var fileInputRef = useRef(null);
    var settingsFileRef = useRef(null);
    var textareaRef = useRef(null);
    var atBottomRef = useRef(true);
    var prevMsgLenRef = useRef(0);
    var streamConvRef = useRef(null);
    var aiAPI = useMemo(function () {
        return new AntAPI('ai', 'AISession');
    }, []);
    var msgAPI = useMemo(function () {
        return new AntAPI('ai', 'AIMessage');
    }, []);
    var docAPI = useMemo(function () {
        return new AntAPI('media', 'MEDDocument');
    }, []);
    var session = AntSession ? AntSession() : null;

    // ─── Load messages ──────────────────────────────────────────
    var loadMessages = useCallback(
        function (convId, opts) {
            var cid = convId || conversationId;
            if (!cid) return;
            msgAPI.post(
                '/' + msgAPI.routeURL + '/list',
                {
                    session_id: cid,
                    pageSize: 50,
                    ordering: [{ name: 'id', type: 'desc' }]
                },
                function (result) {
                    var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
                    if (!(rows instanceof Array)) rows = [];
                    rows.sort(function (a, b) {
                        return String(a.id || '0').padStart(18, '0').localeCompare(String(b.id || '0').padStart(18, '0'));
                    });
                    setMessages(rows);
                    if (opts && opts.clearStreaming) {
                        setIsStreaming(false);
                        setIsTyping(false);
                        setStreamingText('');
                    }
                    if (!(opts && opts.skipScroll)) {
                        setTimeout(function () {
                            if (listRef.current) {
                                listRef.current.scrollTop = listRef.current.scrollHeight;
                                atBottomRef.current = true;
                            }
                        }, 50);
                    }
                }
            );
        },
        [conversationId, msgAPI]
    );

    var loadMessagesRef = useRef(loadMessages);
    loadMessagesRef.current = loadMessages;

    // ─── Socket streaming ───────────────────────────────────────
    useEffect(
        function () {
            if (!session || !session.registerSocketListener) return;
            var listenerKey = 'AISessionStream_LS';
            var onSocketReceived = function (data) {
                if (!data || data.event !== 'AISessionStream') return false;
                if (streamConvRef.current && data.session_id && String(data.session_id) !== String(streamConvRef.current)) return false;
                if (data.session_id && !streamConvRef.current) {
                    streamConvRef.current = data.session_id;
                }
                if (data.status === 'searching') {
                    setStreamingText(AntLocalization.translateText({ vi: 'Đang tra cứu dữ liệu...', en: 'Searching data...', fr: 'Recherche des données...', es: 'Buscando datos...', id: 'Mencari data...', ja: 'データを検索中...', zh: '正在查询数据...', ko: '데이터 조회 중...', hi: 'डेटा खोजा जा रहा है...', ar: 'جارٍ البحث في البيانات...' }));
                    setIsStreaming(true);
                    return true;
                }
                if (data.done) {
                    loadMessagesRef.current(data.session_id || streamConvRef.current, { clearStreaming: true });
                    return true;
                }
                if (data.chunk) {
                    setIsStreaming(true);
                    setStreamingText(function (prev) {
                        return (prev || '') + data.chunk;
                    });
                }
                return true;
            };
            session.registerSocketListener(onSocketReceived, listenerKey);
            return function () {
                session.clearSocketListener(listenerKey);
            };
        },
        [session]
    );

    // ─── Smart scroll ───────────────────────────────────────────
    var handleMsgScroll = useCallback(function () {
        if (!listRef.current) return;
        var el = listRef.current;
        atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    }, []);

    useEffect(
        function () {
            if (messages.length > prevMsgLenRef.current && atBottomRef.current && listRef.current) {
                listRef.current.scrollTop = listRef.current.scrollHeight;
            }
            prevMsgLenRef.current = messages.length;
        },
        [messages.length]
    );

    useEffect(
        function () {
            if ((isTyping || isStreaming) && atBottomRef.current && listRef.current) {
                listRef.current.scrollTop = listRef.current.scrollHeight;
            }
        },
        [isTyping, isStreaming, streamingText]
    );

    useEffect(
        function () {
            setMessages([]);
            setConversationId(null);
            setAiView('chat');
        },
        [context ? context.voucherId : null]
    );

    // ─── Drag-drop ──────────────────────────────────────────────
    var handleDragOver = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
    }, []);
    var handleDragLeave = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    }, []);
    var handleDrop = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setPendingFiles(function (prev) {
                return prev.concat(Array.prototype.slice.call(e.dataTransfer.files));
            });
        }
    }, []);

    // ─── Context params ─────────────────────────────────────────
    var getContextParams = useCallback(
        function () {
            if (!context) return { context_mode: 'global' };
            if (context.voucherType && context.voucherCode)
                return { context_mode: 'voucher', context_type: context.voucherType, context_code: context.voucherCode };
            if (context.variant === 'search' && context.serviceName) {
                var ctxData = {};
                if (context.searchConditions) ctxData.searchConditions = context.searchConditions;
                if (context.searchText) ctxData.searchText = context.searchText;
                return { context_mode: 'search', context_type: context.serviceName, context_data: ctxData };
            }
            return { context_mode: 'global' };
        },
        [context]
    );

    // ─── Settings: save name ────────────────────────────────────
    var handleSaveName = useCallback(
        function () {
            if (!conversationId || !editConvName.trim()) {
                setSavingName(false);
                return;
            }
            setSavingName(true);
            aiAPI.post('/' + aiAPI.routeURL + '/update', { where: { id: conversationId }, update: { title: editConvName.trim() } }, function () {
                setSavingName(false);
            });
        },
        [conversationId, editConvName, aiAPI]
    );

    var handleDeleteConv = useCallback(
        function () {
            if (!conversationId) return;
            aiAPI.post('/' + aiAPI.routeURL + '/delete', { id: conversationId }, function (resp) {
                // Phiên mặc định của hệ thống không xoá được (luật configDelete của AISession): hiện lý do, giữ nguyên phiên đang mở.
                if (resp && typeof resp.error === 'string' && resp.error) {
                    notify(resp.error, 'error', 5000);
                    return;
                }
                setMessages([]);
                setConversationId(null);
                setAiView('chat');
            });
        },
        [conversationId, aiAPI]
    );

    var loadAiDocs = useCallback(
        function () {
            if (!conversationId) {
                setAiDocs([]);
                return;
            }
            setAiDocsLoading(true);
            msgAPI.post(
                '/' + msgAPI.routeURL + '/list',
                { session_id: conversationId, content_type: 'file', pageSize: 50, ordering: [{ name: 'id', type: 'desc' }] },
                function (result) {
                    var rows = (result && result.data && result.data.rows) || (result && result.rows) || [];
                    var docs = [];
                    if (rows instanceof Array) {
                        rows.forEach(function (m) {
                            if (m.meddoc_id && m.meddoc_name && (m.file_full_path || m.file_short_path)) {
                                docs.push({
                                    id: m.meddoc_id,
                                    name: m.meddoc_name,
                                    full_path: m.file_full_path,
                                    short_path: m.file_short_path,
                                    user_name: m.user_name || m.created_user_name,
                                    created_at: m.created_at,
                                    message_id: m.id
                                });
                            }
                        });
                    }
                    setAiDocs(docs);
                    setAiDocsLoading(false);
                }
            );
        },
        [conversationId, msgAPI]
    );

    var handleSettingsFileUpload = useCallback(
        function (e) {
            if (!e.target.files || !e.target.files[0] || !conversationId) return;
            var file = e.target.files[0];
            e.target.value = '';
            var fd = new FormData();
            fd.append('files', file);
            fd.append('session_id', conversationId);
            msgAPI.upload('/' + msgAPI.routeURL + '/createWithAttachment', fd, function () {
                loadAiDocs();
            });
        },
        [conversationId, msgAPI, loadAiDocs]
    );

    var handleDeleteDoc = useCallback(
        function (doc) {
            docAPI.post('/' + docAPI.routeURL + '/delete', { id: doc.id }, function () {
                if (doc.message_id) {
                    msgAPI.post(
                        '/' + msgAPI.routeURL + '/update',
                        { where: { id: doc.message_id }, update: { file_full_path: null, file_short_path: null } },
                        function () {
                            loadAiDocs();
                        }
                    );
                } else {
                    loadAiDocs();
                }
            });
        },
        [docAPI, msgAPI, loadAiDocs]
    );

    // ─── Send text via API ──────────────────────────────────────
    var sendText = useCallback(
        function (text) {
            if (!text || isTyping) return;
            setIsTyping(true);
            streamConvRef.current = null;
            var body = Object.assign({ content: text }, getContextParams());
            if (conversationId) body.session_id = conversationId;
            var canStream = session && session.registerSocketListener;
            if (canStream) body.streaming = true;
            aiAPI.post('/' + aiAPI.routeURL + '/actionSendMessage', body, function (result) {
                var data = (result && result.data) || result || {};
                if (data.conversation && data.conversation.id) {
                    var newConvId = data.conversation.id;
                    setConversationId(newConvId);
                    streamConvRef.current = newConvId;
                    if (!editConvName) setEditConvName((text || '').substring(0, 60));
                }
                if (data.streaming) {
                    loadMessages(data.conversation ? data.conversation.id : conversationId);
                    return;
                }
                setIsTyping(false);
                loadMessages(data.conversation ? data.conversation.id : conversationId);
            });
        },
        [isTyping, conversationId, getContextParams, aiAPI, session, loadMessages, editConvName]
    );

    // ─── Send (text + files) ────────────────────────────────────
    // Upload file TRƯỚC → rồi mới gọi AI
    var handleSend = useCallback(
        function () {
            var text = inputText.trim();
            if ((!text && pendingFiles.length === 0) || isTyping || sending) return;
            setInputText('');
            if (pendingFiles.length > 0 && conversationId) {
                setSending(true);
                var filesToSend = pendingFiles.slice();
                setPendingFiles([]);
                var sendNextFile = function (idx) {
                    if (idx >= filesToSend.length) {
                        setSending(false);
                        if (text) {
                            sendText(text);
                        } else {
                            loadMessages(conversationId);
                        }
                        if (textareaRef.current) textareaRef.current.focus();
                        return;
                    }
                    var fd = new FormData();
                    fd.append('files', filesToSend[idx]);
                    fd.append('session_id', conversationId);
                    msgAPI.upload('/' + msgAPI.routeURL + '/createWithAttachment', fd, function () {
                        sendNextFile(idx + 1);
                    });
                };
                sendNextFile(0);
            } else {
                if (text) {
                    setPendingFiles([]);
                    sendText(text);
                }
            }
        },
        [inputText, pendingFiles, isTyping, sending, conversationId, sendText, msgAPI, loadMessages]
    );

    var handleKeyDown = useCallback(
        function (e) {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
            }
        },
        [handleSend]
    );

    var handleFileSelect = useCallback(function (e) {
        if (!e.target.files || e.target.files.length === 0) return;
        setPendingFiles(function (prev) {
            return prev.concat(Array.prototype.slice.call(e.target.files));
        });
        e.target.value = '';
    }, []);

    var removePendingFile = useCallback(function (idx) {
        setPendingFiles(function (prev) {
            return prev.filter(function (_, i) {
                return i !== idx;
            });
        });
    }, []);

    // var handleAction = useCallback(
    //     function (actionKey) {
    //         if (actionKey === 'cancel') {
    //             setMessages(function (prev) {
    //                 return prev.concat([
    //                     { role: 'user', blocks: [{ type: 'text', text: 'Hủy' }] },
    //                     { role: 'assistant', blocks: [{ type: 'text', text: 'Đã hủy thao tác.' }] }
    //                 ]);
    //             });
    //             return;
    //         }
    //         if (mockMode) {
    //             setMessages(function (prev) {
    //                 return prev.concat([{ role: 'user', blocks: [{ type: 'text', text: 'Xác nhận' }] }]);
    //             });
    //             setIsTyping(true);
    //             setTimeout(function () {
    //                 setMessages(function (prev) {
    //                     return prev.concat([
    //                         { role: 'assistant', blocks: [{ type: 'status', variant: 'success', text: 'Thao tác đã được thực hiện thành công.' }] }
    //                     ]);
    //                 });
    //                 setIsTyping(false);
    //             }, 600);
    //         }
    //     },
    //     [mockMode]
    // );

    var showWelcome = !context || !context.voucherType;

    // ═══ SETTINGS VIEW ══════════════════════════════════════════
    if (aiView === 'settings') {
        return (
            <div className="ai-panel">
                <div className="ai-settings-header">
                    <button
                        className="ai-settings-back"
                        onClick={function () {
                            setAiView('chat');
                            setTimeout(function () {
                                if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
                            }, 50);
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <span className="ai-settings-title">{AntLocalization.translateText({ vi: 'Cấu hình', en: 'Settings', fr: 'Paramètres', id: 'Pengaturan', ja: '設定', zh: '设置', ko: '설정', hi: 'सेटिंग्स', ar: 'الإعدادات', es: 'Ajustes' })}</span>
                </div>
                <div className="ai-settings-body">
                    {/* Rename */}
                    <div className="ai-settings-section">
                        <span className="ai-settings-label">{AntLocalization.translateText({ vi: 'Tên hội thoại', en: 'Name', fr: 'Nom du chat', es: 'Nombre del chat', id: 'Nama percakapan', ja: '会話名', zh: '会话名称', ko: '대화 이름', hi: 'वार्तालाप नाम', ar: 'اسم المحادثة' })}</span>
                        <div className="ai-settings-rename-row">
                            <input
                                className="ai-settings-input"
                                value={editConvName}
                                onChange={function (e) {
                                    setEditConvName(e.target.value);
                                }}
                                onKeyDown={function (e) {
                                    if (e.key === 'Enter') handleSaveName();
                                }}
                                placeholder={AntLocalization.translateText({ vi: 'Nhập tên...', en: 'Enter name...', fr: 'Saisir un nom...', es: 'Nombre...', id: 'Masukkan nama...', ja: '名前を入力...', zh: '输入名称...', ko: '이름 입력...', hi: 'नाम दर्ज करें...', ar: 'أدخل الاسم...' })}
                            />
                            <button className="ai-settings-save" onClick={handleSaveName} disabled={savingName || !editConvName.trim()}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                                    <polyline points="17 21 17 13 7 13 7 21" />
                                    <polyline points="7 3 7 8 15 8" />
                                </svg>
                            </button>
                        </div>
                    </div>
                    {/* Documents */}
                    <div className="ai-settings-section">
                        <div className="ai-settings-label-row">
                            <span className="ai-settings-label">{AntLocalization.translateText({ vi: 'Tài liệu đính kèm', en: 'Attachments', fr: 'Documents joints', es: 'Documentos adjuntos', id: 'Dokumen terlampir', ja: '添付書類', zh: '附件文档', ko: '첨부 문서', hi: 'संलग्न दस्तावेज़', ar: 'المستندات المرفقة' })}</span>
                            <button
                                className="ai-settings-link"
                                onClick={function () {
                                    if (settingsFileRef.current) settingsFileRef.current.click();
                                }}
                            >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 5v14M5 12h14" />
                                </svg>
                                {AntLocalization.translateText({ vi: 'Thêm', en: 'Add', fr: 'Ajouter', es: 'Añadir', id: 'Tambah', ja: '追加', zh: '添加', ko: '추가', hi: 'जोड़ें', ar: 'إضافة' })}
                            </button>
                            <input ref={settingsFileRef} type="file" style={{ display: 'none' }} onChange={handleSettingsFileUpload} />
                        </div>
                        <div className="ai-settings-doc-list">
                            {aiDocsLoading && (
                                <div className="ai-settings-empty">{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '로딩 중...', hi: 'लोड जारी...', ar: 'يتم التحميل...' })}</div>
                            )}
                            {!aiDocsLoading && aiDocs.length === 0 && (
                                <div className="ai-settings-empty">
                                    {AntLocalization.translateText({ vi: 'Chưa có tài liệu', en: 'No documents', fr: 'Aucun fichier', es: 'Sin archivos', id: 'Belum ada file', ja: 'ファイルなし', zh: '暂无文件', ko: '파일 없음', hi: 'कोई फ़ाइल नहीं', ar: 'لا توجد ملفات' })}
                                </div>
                            )}
                            {aiDocs.map(function (d) {
                                var ext = (d.name || '').split('.').pop().toLowerCase();
                                var icon = /^(jpg|jpeg|png|gif|webp|svg)$/.test(ext)
                                    ? '🖼️'
                                    : ext === 'pdf'
                                    ? '📄'
                                    : /^(doc|docx)$/.test(ext)
                                    ? '📝'
                                    : /^(xls|xlsx)$/.test(ext)
                                    ? '📊'
                                    : '📎';
                                return (
                                    <div key={d.id} className="ai-settings-doc-item">
                                        <span className="ai-settings-doc-icon">{icon}</span>
                                        <span
                                            className="ai-settings-doc-name"
                                            onClick={function () {
                                                var viewFiles = aiDocs
                                                    .filter(function (x) {
                                                        return x.full_path;
                                                    })
                                                    .map(function (x) {
                                                        return { name: x.name, url: x.full_path, type: '' };
                                                    });
                                                var viewIdx = 0;
                                                for (var vi = 0; vi < viewFiles.length; vi++) {
                                                    if (viewFiles[vi].url === d.full_path) {
                                                        viewIdx = vi;
                                                        break;
                                                    }
                                                }
                                                if (viewFiles.length > 0) setAiViewerState({ files: viewFiles, index: viewIdx });
                                            }}
                                        >
                                            {d.name}
                                        </span>
                                        <button
                                            className="ai-settings-doc-del"
                                            onClick={function () {
                                                handleDeleteDoc(d);
                                            }}
                                        >
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <path d="M18 6L6 18M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    {/* Danger */}
                    <div className="ai-settings-danger">
                        <button className="ai-settings-delete" onClick={handleDeleteConv}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            {AntLocalization.translateText({ vi: 'Xóa hội thoại', en: 'Delete conversation', fr: 'Supprimer le chat', es: 'Eliminar conversación', id: 'Hapus percakapan', ja: '会話を削除', zh: '删除会话', ko: '대화 삭제', hi: 'वार्तालाप हटाएँ', ar: 'حذف المحادثة' })}
                        </button>
                    </div>
                </div>
                {aiViewerState && (
                    <AntFileViewer
                        visible
                        files={aiViewerState.files}
                        initialIndex={aiViewerState.index}
                        onClose={function () {
                            setAiViewerState(null);
                        }}
                    />
                )}
            </div>
        );
    }

    // ─── File viewer ──────────────────────────────────────────────
    var openFileViewer = useCallback(
        function (m) {
            var allFiles = messages
                .filter(function (x) {
                    return x.meddoc_id && (x.file_full_path || x.file_short_path);
                })
                .map(function (x) {
                    return { name: x.meddoc_name || x.content, url: x.file_full_path || x.file_short_path, type: '' };
                });
            var idx = 0;
            var targetUrl = m.file_full_path || m.file_short_path;
            for (var i = 0; i < allFiles.length; i++) {
                if (allFiles[i].url === targetUrl) {
                    idx = i;
                    break;
                }
            }
            if (allFiles.length > 0) setAiViewerState({ files: allFiles, index: idx });
        },
        [messages]
    );

    // ═══ CHAT VIEW ══════════════════════════════════════════════
    return (
        <div className={'ai-panel' + (dragOver ? ' drag-over' : '')} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
            {dragOver && (
                <div className="ai-drop-overlay">{AntLocalization.translateText({ vi: 'Thả file để đính kèm', en: 'Drop file to attach', fr: 'Déposez pour joindre', es: 'Suelte para adjuntar', id: 'Lepas untuk melampirkan', ja: 'ドロップして添付', zh: '拖放文件以添加附件', ko: '파일을 놓아 첨부', hi: 'फ़ाइल छोड़कर जोड़ें', ar: 'أفلت الملف للإرفاق' })}</div>
            )}
            {/* Header */}
            <div className="ai-chat-header">
                <span className="ai-chat-title">{editConvName || AntLocalization.translateText({ vi: 'Trợ lý AI', en: 'AI Assistant', fr: 'Assistant IA', es: 'Asistente IA', id: 'Asisten AI', ja: 'AIアシスタント', zh: 'AI 助手', ko: 'AI 어시스턴트', hi: 'AI सहायक', ar: 'مساعد AI' })}</span>
                <button
                    className="ai-chat-settings-btn"
                    onClick={function () {
                        setAiView('settings');
                        loadAiDocs();
                    }}
                    title={AntLocalization.translateText({ vi: 'Cấu hình', en: 'Settings', fr: 'Paramètres', id: 'Pengaturan', ja: '設定', zh: '设置', ko: '설정', hi: 'सेटिंग्स', ar: 'الإعدادات', es: 'Ajustes' })}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <circle cx="12" cy="12" r="3" />
                    </svg>
                </button>
            </div>
            {/* Messages */}
            <div className="ai-message-list" ref={listRef} onScroll={handleMsgScroll}>
                {showWelcome && messages.length === 0 && !isTyping && (
                    <div className="ai-welcome">
                        <div className="ai-welcome-text">
                            {AntLocalization.translateText({ vi: 'Trợ lý AI sẵn sàng hỗ trợ bạn.', en: 'AI Assistant is ready to help.', fr: 'L\'assistant IA est prêt à vous aider.', es: 'Asistente IA listo para ayudarle.', id: 'Asisten AI siap membantu Anda.', ja: 'AIアシスタントがお手伝いします。', zh: 'AI 助手已准备好为您服务。', ko: 'AI 어시스턴트가 도와드립니다.', hi: 'AI सहायक आपकी मदद के लिए तैयार है।', ar: 'مساعد AI جاهز لمساعدتك.' })}
                        </div>
                        <div className="ai-welcome-sub">
                            {AntLocalization.translateText({
                                vi: 'Mở chứng từ để bắt đầu tương tác theo ngữ cảnh.',
                                en: 'Open a document to start contextual interaction.',
                                fr: 'Ouvrez un document pour démarrer l\'interaction contextuelle.',
                                es: 'Abra un documento para iniciar la interacción contextual.',
                                id: 'Buka dokumen untuk mulai interaksi sesuai konteks.',
                                ja: '伝票を開くと、文脈に沿った対話を開始できます。',
                                zh: '打开单据以开始基于上下文的交互。',
                                ko: '문서를 열어 문맥 기반 대화를 시작하세요.',
                                hi: 'संदर्भ आधारित संवाद शुरू करने के लिए दस्तावेज़ खोलें।',
                                ar: 'افتح مستندًا لبدء التفاعل حسب السياق.'
                            })}
                        </div>
                    </div>
                )}
                {messages.map(function (m, mi) {
                    var isUser = m.role === 'user';
                    var prev = mi > 0 ? messages[mi - 1] : null;
                    var sameRoleAsPrev = prev && prev.role === m.role;
                    var showHeader = !sameRoleAsPrev;
                    var userName = isUser
                        ? m.created_user_name || m.user_name || AntLocalization.translateText({ vi: 'Bạn', en: 'You', fr: 'Vous', es: 'Usted', id: 'Anda', ja: 'あなた', zh: '您', ko: '나', hi: 'आप', ar: 'أنت' })
                        : AntLocalization.translateText({ vi: 'Trợ lý AI', en: 'AI Assistant', fr: 'Assistant IA', es: 'Asistente IA', id: 'Asisten AI', ja: 'AIアシスタント', zh: 'AI 助手', ko: 'AI 어시스턴트', hi: 'AI सहायक', ar: 'مساعد AI' });
                    var hasMeddoc = m.meddoc_id && m.meddoc_name;
                    var fileUrl = m.file_full_path || m.file_short_path;
                    var isImage = hasMeddoc && fileUrl && /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(m.meddoc_name);
                    var fileExt = hasMeddoc ? (m.meddoc_name || '').split('.').pop().toLowerCase() : '';
                    var fileLabel =
                        fileExt === 'pdf'
                            ? 'PDF'
                            : fileExt === 'doc' || fileExt === 'docx'
                            ? 'DOC'
                            : fileExt === 'xls' || fileExt === 'xlsx'
                            ? 'XLS'
                            : fileExt.toUpperCase();

                    return (
                        <div key={m.id || mi} className={'ai-message' + (isUser ? ' ai-message-user' : ' ai-message-assistant')}>
                            {showHeader && !isUser && <div className="ai-avatar">AI</div>}
                            {showHeader && isUser && (
                                <div className="ai-avatar" style={{ background: '#64748b' }}>
                                    {(userName || '?').charAt(0).toUpperCase()}
                                </div>
                            )}
                            {!showHeader && <div style={{ width: 26, minWidth: 26 }} />}
                            <div className="ai-bubble">
                                {showHeader && (
                                    <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 2 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: isUser ? 'rgba(255,255,255,0.85)' : '#334155' }}>
                                            {userName}
                                        </span>
                                        {m.created_at && (
                                            <span style={{ fontSize: 10, color: isUser ? 'rgba(255,255,255,0.6)' : '#94a3b8' }}>
                                                {commentFormatTime(m.created_at)}
                                            </span>
                                        )}
                                    </div>
                                )}
                                {m.content_type !== 'file' && m.content && <div className="ai-msg-text">{m.content}</div>}
                                {hasMeddoc && fileUrl && isImage && (
                                    <img
                                        style={{ maxWidth: '100%', borderRadius: 6, marginTop: 4, cursor: 'pointer' }}
                                        src={fileUrl}
                                        alt=""
                                        onClick={function () {
                                            openFileViewer(m);
                                        }}
                                    />
                                )}
                                {hasMeddoc && fileUrl && !isImage && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            marginTop: 4,
                                            padding: '6px 8px',
                                            background: 'rgba(0,0,0,0.04)',
                                            borderRadius: 6,
                                            cursor: 'pointer',
                                            fontSize: 12
                                        }}
                                        onClick={function () {
                                            openFileViewer(m);
                                        }}
                                    >
                                        <span style={{ fontWeight: 600 }}>{fileLabel}</span>
                                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {m.meddoc_name}
                                        </span>
                                    </div>
                                )}
                                {hasMeddoc && !fileUrl && (
                                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                        📎 {m.meddoc_name} <em>({AntLocalization.translateText({ vi: 'đã xoá', en: 'deleted', fr: 'supprimé', es: 'eliminado', id: 'dihapus', ja: '削除済み', zh: '已删除', ko: '삭제됨', hi: 'हटाया गया', ar: 'محذوف' })})</em>
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
                {isStreaming && streamingText && (
                    <div className="ai-message ai-message-assistant">
                        <div className="ai-avatar">AI</div>
                        <div className="ai-bubble">
                            <div className="ai-msg-text">{streamingText}</div>
                        </div>
                    </div>
                )}
                {isTyping && !isStreaming && (
                    <div className="ai-message ai-message-assistant">
                        <div className="ai-avatar">AI</div>
                        <div className="ai-bubble ai-typing">
                            <span />
                            <span />
                            <span />
                        </div>
                    </div>
                )}
            </div>
            {/* Input */}
            <div className="ai-input-area">
                {pendingFiles.length > 0 && (
                    <div className="ai-file-preview-list">
                        {pendingFiles.map(function (f, fi) {
                            return (
                                <div key={fi} className="ai-file-preview">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                    </svg>
                                    <span className="ai-file-preview-name">{f.name}</span>
                                    <button
                                        className="ai-file-preview-close"
                                        onClick={function () {
                                            removePendingFile(fi);
                                        }}
                                    >
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className="ai-input-toolbar">
                    <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFileSelect} />
                    <button
                        className="ai-tool-btn"
                        onClick={function () {
                            if (fileInputRef.current) fileInputRef.current.click();
                        }}
                        title={AntLocalization.translateText({ vi: 'Đính kèm', en: 'Attach', fr: 'Joindre', es: 'Adjuntar', id: 'Lampirkan', ja: '添付', zh: '添加附件', ko: '첨부', hi: 'संलग्न करें', ar: 'إرفاق' })}
                    >
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                    </button>
                    <span style={{ flex: 1 }} />
                    <button
                        className={'ai-tool-btn ai-send-btn' + (inputText.trim() || pendingFiles.length > 0 ? ' active' : '')}
                        onClick={handleSend}
                        disabled={(!inputText.trim() && pendingFiles.length === 0) || isTyping || sending}
                        title={AntLocalization.translateText({ vi: 'Gửi', en: 'Send', fr: 'Envoyer', es: 'Enviar', id: 'Kirim', ja: '送信', zh: '发送', ko: '보내기', hi: 'भेजें', ar: 'إرسال' })}
                    >
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                        </svg>
                    </button>
                </div>
                <div className="ai-input-row">
                    <textarea
                        ref={textareaRef}
                        className="ai-input"
                        value={inputText}
                        onChange={function (e) {
                            setInputText(e.target.value);
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder={AntLocalization.translateText({ vi: 'Nhập tin nhắn...', en: 'Type a message...', fr: 'Entrez un message...', es: 'Ingrese mensaje...', id: 'Masukkan pesan...', ja: 'メッセージを入力...', zh: '输入消息...', ko: '메시지 입력...', hi: 'संदेश दर्ज करें...', ar: 'أدخل رسالة...' })}
                        rows={2}
                        disabled={isTyping || sending}
                    />
                </div>
            </div>
            {aiViewerState && (
                <AntFileViewer
                    visible
                    files={aiViewerState.files}
                    initialIndex={aiViewerState.index}
                    onClose={function () {
                        setAiViewerState(null);
                    }}
                />
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── HISTORY PANEL ───────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

var HISTORY_ACTION_CONFIG = {
    create: { label: { vi: 'Tạo mới', en: 'Created', fr: 'Création', es: 'Creación', id: 'Dibuat', ja: '作成', zh: '创建', ko: '생성', hi: 'बनाया', ar: 'إنشاء' }, color: '#22c55e' },
    update: { label: { vi: 'Cập nhật', en: 'Updated', fr: 'Mise à jour', es: 'Actualizado', id: 'Diperbarui', ja: '更新', zh: '更新', ko: '수정', hi: 'अपडेट', ar: 'تحديث' }, color: '#3b82f6' },
    release: { label: { vi: 'Duyệt', en: 'Released', fr: 'Validation', es: 'Aprobado', id: 'Disetujui', ja: '承認', zh: '审批', ko: '승인', hi: 'स्वीकृत', ar: 'اعتماد' }, color: '#8b5cf6' },
    close: { label: { vi: 'Đóng', en: 'Closed', fr: 'Fermer', es: 'Cerrar', id: 'Tutup', ja: '閉じる', zh: '关闭', ko: '닫기', hi: 'बंद करें', ar: 'إغلاق' }, color: '#64748b' },
    cancel: { label: { vi: 'Hủy', en: 'Cancelled', fr: 'Annuler', es: 'Cancelar', id: 'Batal', ja: 'キャンセル', zh: '取消', ko: '취소', hi: 'रद्द करें', ar: 'إلغاء' }, color: '#ef4444' },
    comment: { label: { vi: 'Bình luận', en: 'Comment', fr: 'Commentaires', es: 'Comentarios', id: 'Komentar', ja: 'コメント', zh: '评论', ko: '댓글', hi: 'टिप्पणी', ar: 'التعليقات' }, color: '#0ea5e9' },
    attachment: { label: { vi: 'Đính kèm', en: 'Attachment', fr: 'Pièce jointe', es: 'Adjunto', id: 'Lampiran', ja: '添付', zh: '附件', ko: '첨부', hi: 'संलग्नक', ar: 'مرفق' }, color: '#f59e0b' }
};

function HistoryPanel(props) {
    var mockMode = props.mockMode;
    var [history] = useState(mockMode ? MOCK_HISTORY : []);

    return (
        <div className="history-list">
            {history.length === 0 && (
                <div className="sidebar-panel-empty">
                    <div className="empty-text">{AntLocalization.translateText({ vi: 'Chưa có lịch sử', en: 'No history', fr: 'Aucun historique', es: 'Sin historial', id: 'Belum ada riwayat', ja: '履歴なし', zh: '暂无历史', ko: '내역 없음', hi: 'कोई इतिहास नहीं', ar: 'لا يوجد سجل' })}</div>
                </div>
            )}
            {history.map(function (h) {
                var cfg = HISTORY_ACTION_CONFIG[h.action] || HISTORY_ACTION_CONFIG.update;
                return (
                    <div key={h.id} className="history-item">
                        <div className="history-dot" style={{ backgroundColor: cfg.color }} />
                        <div className="history-content">
                            <div className="history-text">{h.description}</div>
                            <div className="history-meta">
                                <span style={{ color: cfg.color }}>{AntLocalization.translateText(cfg.label)}</span>
                                <span>
                                    {' '}
                                    · {h.user_name} · {commentFormatTime(h.created_at)}
                                </span>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── SEARCH PANELS (Filters, Saved, Stats) ──────────────────────
// ═══════════════════════════════════════════════════════════════════

function FiltersPanel(props) {
    var mockMode = props.mockMode;
    var onFilterGroupChange = props.onFilterGroupChange;
    var [groups, setGroups] = useState(mockMode ? MOCK_FILTER_GROUPS : []);

    var handleClick = useCallback(
        function (groupKey) {
            setGroups(function (prev) {
                return prev.map(function (g) {
                    return Object.assign({}, g, { active: g.key === groupKey });
                });
            });
            if (onFilterGroupChange) onFilterGroupChange(groupKey);
        },
        [onFilterGroupChange]
    );

    return (
        <div className="filter-group-list">
            {groups.map(function (g) {
                return (
                    <div
                        key={g.key}
                        className={'filter-group-item' + (g.active ? ' active' : '')}
                        onClick={function () {
                            handleClick(g.key);
                        }}
                    >
                        <span>{typeof g.label === 'object' ? AntLocalization.translateText(g.label) : g.label}</span>
                        {g.count != null && <span className="filter-group-count">{g.count}</span>}
                    </div>
                );
            })}
        </div>
    );
}

function SavedPanel(props) {
    var mockMode = props.mockMode;
    var variant = props.variant;
    var [views] = useState(mockMode ? (variant === 'report' ? MOCK_SAVED_REPORTS : MOCK_SAVED_VIEWS) : []);

    return (
        <div className="saved-view-list">
            {views.length === 0 && (
                <div className="sidebar-panel-empty">
                    <div className="empty-text">{AntLocalization.translateText({ vi: 'Chưa có mục đã lưu', en: 'No saved items', fr: 'Rien d\'enregistré', es: 'Nada guardado', id: 'Belum ada simpanan', ja: '保存項目なし', zh: '暂无已保存项', ko: '저장된 항목 없음', hi: 'कोई सहेजा आइटम नहीं', ar: 'لا عناصر محفوظة' })}</div>
                </div>
            )}
            {views.map(function (v) {
                return (
                    <div key={v.id} className="saved-view-item">
                        <div className="saved-view-icon">
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                            </svg>
                        </div>
                        <span className="saved-view-name">{v.name}</span>
                    </div>
                );
            })}
        </div>
    );
}

function StatsPanel(props) {
    var mockMode = props.mockMode;
    var [stats] = useState(mockMode ? MOCK_STATS : []);
    var maxValue = useMemo(
        function () {
            var m = 0;
            stats.forEach(function (s) {
                if (s.value > m) m = s.value;
            });
            return m || 1;
        },
        [stats]
    );

    return (
        <div className="stats-panel">
            {stats.map(function (s, idx) {
                return (
                    <div key={idx} className="stats-bar-item">
                        <span className="stats-bar-label">{typeof s.label === 'object' ? AntLocalization.translateText(s.label) : s.label}</span>
                        <div className="stats-bar-track">
                            <div
                                className="stats-bar-fill"
                                style={{ width: Math.round((s.value / maxValue) * 100) + '%', backgroundColor: s.color }}
                            />
                        </div>
                        <span className="stats-bar-count">{s.value}</span>
                    </div>
                );
            })}
        </div>
    );
}

function ParamsPanel(props) {
    var mockMode = props.mockMode;
    var [params, setParams] = useState(mockMode ? MOCK_REPORT_PARAMS : []);
    var handleChange = useCallback(function (key, value) {
        setParams(function (prev) {
            return prev.map(function (p) {
                return p.key === key ? Object.assign({}, p, { value: value }) : p;
            });
        });
    }, []);

    return (
        <div className="params-panel">
            {params.map(function (p) {
                var label = typeof p.label === 'object' ? AntLocalization.translateText(p.label) : p.label;
                return (
                    <div key={p.key} className="params-field">
                        <label className="params-label">{label}</label>
                        {p.type === 'date' && (
                            <input
                                type="date"
                                className="params-input"
                                value={p.value || ''}
                                onChange={function (e) {
                                    handleChange(p.key, e.target.value);
                                }}
                            />
                        )}
                        {p.type === 'select' && (
                            <select
                                className="params-select"
                                value={p.value || ''}
                                onChange={function (e) {
                                    handleChange(p.key, e.target.value);
                                }}
                            >
                                {(p.options || []).map(function (opt) {
                                    return (
                                        <option key={opt} value={opt}>
                                            {opt}
                                        </option>
                                    );
                                })}
                            </select>
                        )}
                        {p.type === 'text' && (
                            <input
                                type="text"
                                className="params-input"
                                value={p.value || ''}
                                onChange={function (e) {
                                    handleChange(p.key, e.target.value);
                                }}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════
// ─── MAIN COMPONENT ─────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

var AntLeftSidebar = forwardRef(function AntLeftSidebar(props, ref) {
    var variant = props.variant || 'voucher';
    var mockMode = props.mockMode !== undefined ? props.mockMode : MOCK_MODE;
    var onFilterGroupChange = props.onFilterGroupChange;

    // ─── Context + mutual exclusion ─────────────────────────────
    var sidebarContext = AntSidebar();
    var baseContext = (sidebarContext && sidebarContext.context) || null;
    var [contextOverride, setContextOverride] = useState(null);
    var pageContext = contextOverride || baseContext;

    // Reset override khi voucher thay đổi
    useEffect(
        function () {
            setContextOverride(null);
        },
        [baseContext ? baseContext.voucherId : null]
    );

    // ─── Browse panel (prepended when browseConfig provided) ─────
    var browseConfig = props.browseConfig;
    var browsePanel = useMemo(
        function () {
            if (!browseConfig || !browseConfig.editorName) return null;
            return Object.assign({}, BROWSE_PANEL, {
                label: browseConfig.label || BROWSE_PANEL.label
            });
        },
        [browseConfig]
    );

    // ─── Panels ──────────────────────────────────────────────────
    // aiWizard visibility = ĐỒNG NHẤT với actionUpdateWithDoc trong AntToolbar.
    // Đọc TRỰC TIẾP `props.data.summary.aiExtract` (chính là mainData mà AntVoucher
    // truyền vào — cùng object AntToolbar nhận qua parentData). KHÔNG đi qua
    // pageContext vì context update qua useEffect deps [voucherId, voucherCode],
    // mainData thay đổi (vd canAnalyze toggle) mà id giữ nguyên → context stale →
    // sidebar lệch toolbar.
    var summaryData = props.data && props.data.summary;
    var summaryAIExtract = summaryData && summaryData.aiExtract;
    var panels = useMemo(
        function () {
            var basePanels;
            if (props.panels) basePanels = props.panels;
            else if (variant === 'search') basePanels = SEARCH_PANELS;
            else if (variant === 'report') basePanels = REPORT_PANELS;
            else
                basePanels = VOUCHER_PANELS.filter(function (p) {
                    if (p.key === 'aiWizard') {
                        return isAIWizardAvailable(summaryAIExtract);
                    }
                    var isValid = AntCondition.isVisible({}, p);
                    if (!isValid) return false;
                    return true;
                });
            return browsePanel ? [browsePanel].concat(basePanels) : basePanels;
        },
        [variant, props.panels, summaryAIExtract, browsePanel]
    );

    var defaultPanel = variant === 'search' ? 'filters' : variant === 'report' ? 'params' : 'comment';

    // ─── Screen key — namespace localStorage per màn hình ───────
    // Ưu tiên voucherType (chi tiết phiếu), fallback entityName (search/report)
    var screenKey =
        (pageContext && pageContext.voucherType) ||
        (props.data && props.data.entityName) ||
        null;

    // ─── State ───────────────────────────────────────────────────
    var saved = useMemo(function () { return loadState(screenKey); }, []); // initial mount only
    var [isOpen, setIsOpen] = useState(function () {
        if (saved && saved.isOpen !== undefined) return saved.isOpen;
        return false;
    });
    var [activePanel, setActivePanel] = useState(function () {
        return (saved && saved.activePanel) || defaultPanel;
    });
    var [width, setWidth] = useState(function () {
        return (saved && saved.width) || DEFAULT_WIDTH;
    });
    var [isResizing, setIsResizing] = useState(false);
    var [panelRefreshKey, setPanelRefreshKey] = useState(0);
    var [tabBadges, setTabBadges] = useState({});
    var session = AntSession ? AntSession() : null;

    // ─── Browse panel ref (for imperative refreshRow from AntVoucher) ────
    var browsePanelRef = useRef(null);
    useImperativeHandle(
        ref,
        function () {
            return {
                refreshBrowseRow: function (code, partial) {
                    var bp = browsePanelRef.current;
                    if (bp && typeof bp.refreshRow === 'function') bp.refreshRow(code, partial);
                },
                reloadBrowse: function () {
                    var bp = browsePanelRef.current;
                    if (bp && typeof bp.reload === 'function') bp.reload();
                }
            };
        },
        []
    );

    useEffect(
        function () {
            var validKeys = panels.map(function (p) {
                return p.key;
            });
            if (validKeys.indexOf(activePanel) < 0) setActivePanel(defaultPanel);
        },
        [variant, panels]
    );

    // Khi screenKey thay đổi (điều hướng sang màn khác mà component vẫn sống), reload state.
    var screenKeyRef = useRef(screenKey);
    var skipNextPersistRef = useRef(false);
    useEffect(
        function () {
            if (screenKeyRef.current === screenKey) return;
            screenKeyRef.current = screenKey;
            skipNextPersistRef.current = true; // ngăn persist effect ghi state cũ đè bucket mới
            var s = loadState(screenKey);
            setIsOpen(s && s.isOpen !== undefined ? s.isOpen : false);
            setActivePanel((s && s.activePanel) || defaultPanel);
            setWidth((s && s.width) || DEFAULT_WIDTH);
        },
        [screenKey, defaultPanel]
    );

    useEffect(
        function () {
            if (screenKeyRef.current !== screenKey) return;
            if (skipNextPersistRef.current) {
                skipNextPersistRef.current = false;
                return;
            }
            saveState(screenKey, { isOpen: isOpen, activePanel: activePanel, width: width });
        },
        [screenKey, isOpen, activePanel, width]
    );

    // ─── Badge: check unread comments khi mở phiếu ──────────────
    var notifyAPI = useMemo(function () {
        return new AntAPI('user', 'ACUserNotification');
    }, []);

    var checkCommentBadge = useCallback(
        function () {
            if (!pageContext || !pageContext.voucherType || !pageContext.voucherCode) return;
            notifyAPI.post(
                '/' + notifyAPI.routeURL + '/count',
                {
                    object_type: pageContext.voucherType,
                    object_value: pageContext.voucherCode,
                    viewed: 0
                },
                function (result) {
                    var count = (result && result.data && result.data.count) || (result && result.data) || 0;
                    if (typeof count === 'object') count = count.count || 0;
                    if (count > 0) {
                        setTabBadges(function (prev) {
                            if (prev.comment) return prev;
                            return Object.assign({}, prev, { comment: true });
                        });
                        // Có comment chưa đọc → tự mở tab bình luận + focus input
                        setActivePanel('comment');
                        setIsOpen(true);
                        setPanelRefreshKey(function (k) {
                            return k + 1;
                        });
                        pendingFocusRef.current = true;
                        if (sidebarContext && sidebarContext.setLeftSidebarOpen) sidebarContext.setLeftSidebarOpen(true);
                        if (sidebarContext && sidebarContext.setRightSidebarOpen) sidebarContext.setRightSidebarOpen(false);
                        if (sidebarContext && sidebarContext.setMenuOpened) sidebarContext.setMenuOpened(false);
                    }
                }
            );
        },
        [pageContext, sidebarContext]
    );

    // Check khi mount + khi đổi phiếu
    useEffect(
        function () {
            setTabBadges({});
            checkCommentBadge();
        },
        [pageContext ? pageContext.voucherId : null]
    );

    // ─── Badge: socket listener cho phiếu hiện tại ──────────────
    useEffect(
        function () {
            if (!session || !session.registerSocketListener || !pageContext) return;
            var voucherType = pageContext.voucherType;
            var voucherCode = pageContext.voucherCode;
            if (!voucherType) return;

            var handler = function (data) {
                if (!data) return false;
                // NewComment trên phiếu đang mở
                if (data.event === 'NewComment' && data.voucher_type === voucherType && data.voucher_code === voucherCode) {
                    if (!(isOpen && activePanel === 'comment')) {
                        setTabBadges(function (prev) {
                            if (prev.comment) return prev;
                            return Object.assign({}, prev, { comment: true });
                        });
                    }
                    return false;
                }
                // Data change trên chính phiếu này → badge cho history
                if (data.source === voucherType || data.name === voucherType) {
                    if (!(isOpen && activePanel === 'history')) {
                        setTabBadges(function (prev) {
                            if (prev.history) return prev;
                            return Object.assign({}, prev, { history: true });
                        });
                    }
                    return false;
                }
                return false;
            };
            session.registerSocketListener(handler, 'AntLeftSidebarBadge');
            return function () {
                session.clearSocketListener('AntLeftSidebarBadge');
            };
        },
        [session, pageContext, activePanel, isOpen]
    );

    // Clear badge khi mở tab
    useEffect(
        function () {
            if (isOpen && activePanel) {
                setTabBadges(function (prev) {
                    if (!prev[activePanel]) return prev;
                    var next = Object.assign({}, prev);
                    delete next[activePanel];
                    return next;
                });
            }
        },
        [activePanel, isOpen]
    );

    // ─── Mutual exclusion ──────────────────────────────────────────
    var openLeft = useCallback(
        function () {
            setIsOpen(true);
            if (!sidebarContext) return;
            // Thông báo left đã mở → right sidebar sẽ tự đóng
            if (sidebarContext.setLeftSidebarOpen) sidebarContext.setLeftSidebarOpen(true);
            // Đóng navigation drawer
            if (sidebarContext.setMenuOpened) sidebarContext.setMenuOpened(false);
        },
        [sidebarContext]
    );

    var closeLeft = useCallback(
        function () {
            setIsOpen(false);
            if (sidebarContext && sidebarContext.setLeftSidebarOpen) sidebarContext.setLeftSidebarOpen(false);
        },
        [sidebarContext]
    );

    var handleToggle = useCallback(
        function () {
            if (isOpen) closeLeft();
            else openLeft();
        },
        [isOpen, openLeft, closeLeft]
    );

    var handlePanelClick = useCallback(
        function (panelKey) {
            if (activePanel === panelKey && isOpen) {
                closeLeft();
            } else {
                setActivePanel(panelKey);
                if (!isOpen) openLeft();
                setPanelRefreshKey(function (k) {
                    return k + 1;
                });
            }
        },
        [activePanel, isOpen, openLeft, closeLeft]
    );

    // Lắng nghe right sidebar mở → đóng left
    useEffect(
        function () {
            if (sidebarContext && sidebarContext.rightSidebarOpen && isOpen) {
                setIsOpen(false);
                if (sidebarContext.setLeftSidebarOpen) sidebarContext.setLeftSidebarOpen(false);
            }
        },
        [sidebarContext ? sidebarContext.rightSidebarOpen : null]
    );

    // Lắng nghe pendingLeftPanel → mở tab được yêu cầu (từ notification click, unread comment, v.v.)
    var pendingFocusRef = useRef(false);
    useEffect(
        function () {
            if (!sidebarContext || !sidebarContext.pendingLeftPanel) return;
            var pending = sidebarContext.pendingLeftPanel;
            setActivePanel(pending.panel);
            setIsOpen(true);
            setPanelRefreshKey(function (k) {
                return k + 1;
            });
            if (sidebarContext.setLeftSidebarOpen) sidebarContext.setLeftSidebarOpen(true);
            if (sidebarContext.setRightSidebarOpen) sidebarContext.setRightSidebarOpen(false);
            if (sidebarContext.setMenuOpened) sidebarContext.setMenuOpened(false);
            // Clear badge cho tab này
            setTabBadges(function (prev) {
                if (!prev[pending.panel]) return prev;
                var next = Object.assign({}, prev);
                delete next[pending.panel];
                return next;
            });
            pendingFocusRef.current = !!pending.focus;
            if (sidebarContext.clearPendingLeftPanel) sidebarContext.clearPendingLeftPanel();
        },
        [sidebarContext ? sidebarContext.pendingLeftPanel : null]
    );

    // ─── Resize (RIGHT edge) ────────────────────────────────────
    var resizeRef = useRef({ startX: 0, startWidth: 0 });

    var handleResizeStart = useCallback(
        function (e) {
            e.preventDefault();
            setIsResizing(true);
            resizeRef.current.startX = e.clientX;
            resizeRef.current.startWidth = width;

            function onMove(ev) {
                var diff = ev.clientX - resizeRef.current.startX;
                setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, resizeRef.current.startWidth + diff)));
            }
            function onUp() {
                setIsResizing(false);
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
            }
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
        [width]
    );

    // ─── Switch panel ────────────────────────────────────────────
    var handleSwitchPanel = useCallback(
        function (panelKey) {
            setActivePanel(panelKey);
            if (!isOpen) openLeft();
        },
        [isOpen, openLeft]
    );

    // ─── Render panel content ───────────────────────────────────
    var renderPanel = function () {
        var shouldFocusComment = pendingFocusRef.current && activePanel === 'comment';
        if (shouldFocusComment) pendingFocusRef.current = false;
        if (activePanel === 'browse') return <BrowsePanel ref={browsePanelRef} config={browseConfig} />;
        if (activePanel === 'comment')
            return <CommentPanel context={pageContext} mockMode={mockMode} onSwitchPanel={handleSwitchPanel} autoFocus={shouldFocusComment} />;
        if (activePanel === 'attachment') return <AttachmentPanel context={pageContext} mockMode={mockMode} />;
        if (activePanel === 'task')
            return (
                <TaskPanel
                    context={pageContext}
                    onSwitchPanel={function (panel, ctx) {
                        if (ctx) setContextOverride(ctx);
                        handleSwitchPanel(panel);
                    }}
                />
            );
        if (activePanel === 'aiWizard') return <AIWizardPanel context={pageContext} />;
        if (activePanel === 'ai') return <AIPanel context={pageContext} mockMode={mockMode} />;
        if (activePanel === 'history') return <HistoryPanel mockMode={mockMode} />;
        if (activePanel === 'filters') return <FiltersPanel mockMode={mockMode} onFilterGroupChange={onFilterGroupChange} />;
        if (activePanel === 'saved') return <SavedPanel mockMode={mockMode} variant={variant} />;
        if (activePanel === 'stats') return <StatsPanel mockMode={mockMode} />;
        if (activePanel === 'params') return <ParamsPanel mockMode={mockMode} />;
        return null;
    };

    var activePanelDef =
        panels.find(function (p) {
            return p.key === activePanel;
        }) || panels[0];

    // ─── Chặn wheel event không lan ra DevExtreme ScrollView ────
    // React onWheel (synthetic) KHÔNG đủ vì DevExtreme dxScrollable attach native wheel listener.
    // Phải attach native non-passive listener trên DOM thật để stopPropagation chặn được DX trước khi nó nuốt event.
    var rootRef = useRef(null);
    useEffect(function () {
        var el = rootRef.current;
        if (!el) return;
        function onWheel(e) { e.stopPropagation(); }
        el.addEventListener('wheel', onWheel, { passive: false });
        return function () { el.removeEventListener('wheel', onWheel); };
    }, []);

    // ─── Render ──────────────────────────────────────────────────
    // Layout: [IconStrip BÊN TRÁI] [Content] [Resize handle]
    return (
        <div
            ref={rootRef}
            className={'ant-left-sidebar' + (isOpen ? ' open' : ' collapsed') + (isResizing ? ' resizing' : '')}
            data-active-panel={activePanel || ''}
            style={{ width: isOpen ? width + COLLAPSED_WIDTH : COLLAPSED_WIDTH, display: panels.length > 0 ? 'flex' : 'none' }}
        >
            {/* Icon strip (BÊN TRÁI - luôn hiển thị) */}
            {panels.length > 0 && (
                <div className="ls-icon-strip">
                    {panels.map(function (panel) {
                        var isActive = activePanel === panel.key && isOpen;
                        var hasBadge = !!tabBadges[panel.key];
                        return (
                            <TooltipBtn
                                key={panel.key}
                                className={'ls-icon-btn' + (isActive ? ' active' : '')}
                                onClick={function () {
                                    handlePanelClick(panel.key);
                                }}
                                tooltip={AntLocalization.translateText(panel.label)}
                                side="right"
                            >
                                <div className="ls-icon-wrap">
                                    <SvgIcon d={panel.iconPath} size={18} color={isActive ? 'var(--base-accent, #4f7cff)' : '#64748b'} />
                                    {hasBadge && <span className="ls-badge-dot" />}
                                </div>
                            </TooltipBtn>
                        );
                    })}
                    <div className="ls-icon-separator" />
                    <TooltipBtn
                        className="ls-icon-btn toggle-btn"
                        onClick={handleToggle}
                        tooltip={
                            isOpen
                                ? AntLocalization.translateText({ vi: 'Thu gọn', en: 'Collapse', fr: 'Réduire', es: 'Contraer', id: 'Ciutkan', ja: '折りたたむ', zh: '收起', ko: '접기', hi: 'समेटें', ar: 'طي' })
                                : AntLocalization.translateText({ vi: 'Mở rộng', en: 'Expand', fr: 'Développer', es: 'Expandir', id: 'Perluas', ja: '展開', zh: '展开', ko: '펼치기', hi: 'फैलाएँ', ar: 'توسيع' })
                        }
                        side="right"
                    >
                        <SvgIcon d={isOpen ? chevronLeftPath : chevronRightPath} size={16} />
                    </TooltipBtn>
                </div>
            )}

            {/* Content (BÊN PHẢI icon strip) */}
            {isOpen && (
                <div className="ls-content" style={{ width: width }}>
                    <div className="ls-panel-header">
                        <span className="panel-title">{activePanelDef ? AntLocalization.translateText(activePanelDef.label) : ''}</span>
                        <button className="panel-close-btn" onClick={handleToggle}>
                            <SvgIcon d={chevronLeftPath} size={14} />
                        </button>
                    </div>
                    <div className="ls-panel-body" key={activePanel + '-' + panelRefreshKey}>
                        {renderPanel()}
                    </div>
                </div>
            )}

            {/* Resize handle (cạnh phải) */}
            {isOpen && <div className="ls-resize-handle" onMouseDown={handleResizeStart} />}
        </div>
    );
});

export default AntLeftSidebar;
